import React from "react";

import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";

import logo from "./zpa.png";
import studentAvatar from "./goldavartar.png";

/* ============================================================================
   PERIOD CONSTANTS
============================================================================ */

const sem1Periods = [
  "Period 1",
  "Period 2",
  "Period 3",
  "Exam",
];

const sem2Periods = [
  "Period 4",
  "Period 5",
  "Period 6",
  "Exam2",
];

const allPeriods = [...sem1Periods, ...sem2Periods];

/* ============================================================================
   SUBJECT ORDERING
============================================================================ */

const SUBJECT_ORDER = {
  ECE: [
    "Health Science",
    "History",
    "English Writing",
    "English Reciting",
    "Mathematic Reciting",
    "Mathematic Writing",
    "Reading",
    "Social Studies",
    "Phonics",
    "Bible/RME",
    "Spelling",
    "Arts/Drawing",
    "General Science",
  ],

  Elementary: [
    "Math",
    "English",
    "Reading",
    "Writing",
    "Phonics",
    "Spelling",
    "General Science",
    "Social Studies",
    "Bible/RME",
    "Physical Education",
    "Arts/Drawing",
    "Health Science",
  ],
};

/* ============================================================================
   COLORS
============================================================================ */

const COLORS = {
  navy: "#092D50",
  deepNavy: "#061F38",
  blue: "#174F7D",
  blueText: "#15558A",
  blueBorder: "#B8D2E5",
  lightBlue: "#EDF5FB",
  paleBlue: "#F5F9FC",

  red: "#C62828",
  lightRed: "#FCEDEE",

  gold: "#D5A83B",
  lightGold: "#FFF8E6",

  green: "#39834A",
  lightGreen: "#EDF7EF",

  white: "#FFFFFF",
  black: "#17212B",

  gray: "#687787",
  darkGray: "#344454",
  mediumGray: "#A3AFBA",
  lightGray: "#F3F6F8",
  border: "#D5DEE6",
};

/* ============================================================================
   SCORE COLORS
============================================================================ */

const getColor = (value) => {
  if (
    value === "N/A" ||
    value === undefined ||
    value === null ||
    value === ""
  ) {
    return COLORS.mediumGray;
  }

  const textValue = String(value).trim().toUpperCase();

  if (textValue === "F") {
    return COLORS.red;
  }

  if (["A", "B", "C", "D", "E"].includes(textValue)) {
    return COLORS.blueText;
  }

  const numericValue = Number(value);

  if (!Number.isNaN(numericValue)) {
    return numericValue < 70
      ? COLORS.red
      : COLORS.blueText;
  }

  return COLORS.blueText;
};

const getScoreBackground = (value) => {
  if (
    value === "N/A" ||
    value === undefined ||
    value === null ||
    value === ""
  ) {
    return COLORS.lightGray;
  }

  const numericValue = Number(value);

  if (!Number.isNaN(numericValue) && numericValue < 70) {
    return COLORS.lightRed;
  }

  if (!Number.isNaN(numericValue) && numericValue >= 90) {
    return COLORS.lightGreen;
  }

  return COLORS.lightBlue;
};

const getScoreBorder = (value) => {
  if (
    value === "N/A" ||
    value === undefined ||
    value === null ||
    value === ""
  ) {
    return COLORS.border;
  }

  const numericValue = Number(value);

  if (!Number.isNaN(numericValue) && numericValue < 70) {
    return COLORS.red;
  }

  return COLORS.blueText;
};

/* ============================================================================
   FORMATTER
============================================================================ */

const formatValue = (value) => {
  if (
    value === undefined ||
    value === null ||
    value === ""
  ) {
    return "N/A";
  }

  return String(value);
};

/* ============================================================================
   RANK FRACTION HELPER

   Backend example:
   "2/10"

   Displays:
       2
      ---
       10
============================================================================ */

const getRankParts = (rank) => {
  if (
    rank === undefined ||
    rank === null ||
    rank === "" ||
    String(rank).trim().toUpperCase() === "N/A"
  ) {
    return null;
  }

  const parts = String(rank).trim().split("/");

  if (
    parts.length !== 2 ||
    !parts[0].trim() ||
    !parts[1].trim()
  ) {
    return null;
  }

  return {
    numerator: parts[0].trim(),
    denominator: parts[1].trim(),
  };
};

/* ============================================================================
   FRONT PAGE STYLES
============================================================================ */

const styles = StyleSheet.create({

  page: {
    paddingTop: 10,
    paddingBottom: 9,
    paddingHorizontal: 17,
    backgroundColor: COLORS.white,
    fontFamily: "Helvetica",
    fontSize: 9,
    color: COLORS.black,
  },

  backPage: {
    paddingTop: 17,
    paddingBottom: 15,
    paddingHorizontal: 22,
    backgroundColor: COLORS.white,
    fontFamily: "Helvetica",
    fontSize: 9,
    color: COLORS.black,
  },

  topAccentBar: {
    height: 4,
    width: "100%",
    backgroundColor: COLORS.gold,
    marginBottom: 5,
    borderRadius: 2,
  },

  /* FRONT HEADER */

  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: COLORS.paleBlue,
    borderWidth: 1,
    borderColor: COLORS.blueBorder,
    borderRadius: 6,
    paddingVertical: 5,
    paddingHorizontal: 9,
    minHeight: 57,
  },

  logoSection: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },

  logo: {
    width: 46,
    height: 45,
    objectFit: "contain",
    marginRight: 8,
  },

  schoolIdentity: {
    flex: 1,
  },

  schoolName: {
    fontSize: 13,
    fontWeight: "bold",
    color: COLORS.navy,
    marginBottom: 2,
  },

  schoolSubtitle: {
    fontSize: 6.5,
    fontWeight: "bold",
    color: COLORS.blueText,
    marginBottom: 2,
  },

  schoolLocation: {
    fontSize: 6.5,
    color: COLORS.gray,
  },

  reportTitleSection: {
    alignItems: "flex-end",
    marginLeft: 5,
    marginRight: 10,
  },

  reportCardTitle: {
    fontSize: 13,
    fontWeight: "bold",
    color: COLORS.navy,
    textAlign: "right",
    marginBottom: 2,
  },

  reportCardSubtitle: {
    fontSize: 6.5,
    color: COLORS.gray,
    textAlign: "right",
  },

  frontAvatar: {
    width: 44,
    height: 48,
    objectFit: "cover",
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: COLORS.gold,
  },

  /* STUDENT INFORMATION */

  studentInfoBar: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: COLORS.lightGray,
    borderLeftWidth: 3,
    borderLeftColor: COLORS.gold,
    borderRadius: 4,
    paddingVertical: 5,
    paddingHorizontal: 8,
    marginTop: 5,
    marginBottom: 4,
  },

  studentInfoItem: {
    flex: 1,
    paddingRight: 4,
  },

  studentInfoLabel: {
    fontSize: 6,
    color: COLORS.gray,
    fontWeight: "bold",
    marginBottom: 2,
  },

  studentInfoValue: {
    fontSize: 8,
    fontWeight: "bold",
    color: COLORS.navy,
  },

  /* SECTION HEADER */

  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: COLORS.navy,
    borderRadius: 3,
    paddingVertical: 4,
    paddingHorizontal: 8,
    marginTop: 3,
    marginBottom: 4,
  },

  sectionTitle: {
    fontSize: 8,
    fontWeight: "bold",
    color: COLORS.white,
  },

  sectionTag: {
    fontSize: 6,
    fontWeight: "bold",
    color: COLORS.gold,
  },

  /* ACADEMIC TABLE */

  academicTableWrapper: {
    flexDirection: "row",
    width: "100%",
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 4,
    overflow: "hidden",
  },

  academicHalfTable: {
    width: "50%",
  },

  academicHalfTableRight: {
    width: "50%",
    borderLeftWidth: 1.5,
    borderLeftColor: COLORS.white,
  },

  academicHeaderRow: {
    flexDirection: "row",
    minHeight: 23,
    alignItems: "stretch",
    backgroundColor: COLORS.white,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.navy,
  },

  academicBodyRow: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: COLORS.white,
  },

  /* SUBJECT HEADER - NO DARK BACKGROUND */

  academicSubjectHeaderCell: {
    width: "37.5%",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    paddingHorizontal: 2,
    borderRightWidth: 0.6,
    borderColor: COLORS.border,
    fontSize: 7.5,
    fontWeight: "bold",
    color: COLORS.navy,
  },

  /* SUBJECT NAME - CENTERED */

  academicSubjectCell: {
    width: "37.5%",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    paddingHorizontal: 3,
    borderRightWidth: 0.6,
    borderBottomWidth: 0.6,
    borderColor: COLORS.border,
    fontSize: 8.5,
    fontWeight: "bold",
    color: COLORS.darkGray,
  },

  /* PERIOD HEADINGS - NO DARK BACKGROUND */

  academicScoreHeaderCell: {
    width: "12.5%",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    paddingHorizontal: 1,
    borderRightWidth: 0.6,
    borderColor: COLORS.border,
    fontSize: 6.5,
    fontWeight: "bold",
    color: COLORS.navy,
  },

  academicScoreHeaderRight: {
    width: "16.6667%",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    paddingHorizontal: 1,
    borderRightWidth: 0.6,
    borderColor: COLORS.border,
    fontSize: 6.5,
    fontWeight: "bold",
    color: COLORS.navy,
  },

  /* SCORE CELLS */

  academicScoreCell: {
    width: "12.5%",
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 1,
    borderRightWidth: 0.6,
    borderBottomWidth: 0.6,
    borderColor: COLORS.border,
  },

  academicScoreCellRight: {
    width: "16.6667%",
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 1,
    borderRightWidth: 0.6,
    borderBottomWidth: 0.6,
    borderColor: COLORS.border,
  },

  semesterAverageCell: {
    backgroundColor: COLORS.lightBlue,
  },

  yearlyAverageCell: {
    backgroundColor: COLORS.lightGold,
  },

  /* SCORE CIRCLES */

  scoreCircle: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 1.1,
    justifyContent: "center",
    alignItems: "center",
    alignSelf: "center",
  },

  scoreCircleText: {
    fontSize: 8.5,
    fontWeight: "bold",
    textAlign: "center",
  },

  scoreCircleSmall: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 1,
    justifyContent: "center",
    alignItems: "center",
    alignSelf: "center",
  },

  scoreCircleSmallText: {
    fontSize: 7.5,
    fontWeight: "bold",
    textAlign: "center",
  },

  /* PERIOD AVERAGES */

  periodAverageRow: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: COLORS.lightGray,
    minHeight: 24,
  },

  averageLabel: {
    fontSize: 5.5,
    color: COLORS.gray,
    textAlign: "center",
    marginBottom: 1,
  },

  averageText: {
    fontSize: 7,
    fontWeight: "bold",
    textAlign: "center",
  },

  /* PERFORMANCE SUMMARY */

  performanceSummary: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "stretch",
    width: "78%",
    alignSelf: "center",
    marginTop: 5,
    marginBottom: 1,
  },

  summaryCard: {
    flex: 1,
    marginHorizontal: 4,
    backgroundColor: COLORS.lightBlue,
    borderWidth: 1,
    borderColor: COLORS.blueBorder,
    borderRadius: 4,
    paddingVertical: 4,
    alignItems: "center",
    justifyContent: "center",
  },

  summaryCardLast: {
    flex: 1,
    marginHorizontal: 4,
    backgroundColor: COLORS.lightGold,
    borderWidth: 1,
    borderColor: COLORS.gold,
    borderRadius: 4,
    paddingVertical: 4,
    alignItems: "center",
    justifyContent: "center",
  },

  summaryLabel: {
    fontSize: 6,
    fontWeight: "bold",
    color: COLORS.gray,
    marginBottom: 2,
  },

  summaryValue: {
    fontSize: 14,
    fontWeight: "bold",
  },

  /* PROMOTION POLICY */

  promotionBox: {
    backgroundColor: COLORS.lightGold,
    borderLeftWidth: 3,
    borderLeftColor: COLORS.gold,
    borderRadius: 3,
    paddingVertical: 4,
    paddingHorizontal: 8,
    marginTop: 4,
  },

  promotionTitle: {
    fontSize: 6.5,
    fontWeight: "bold",
    color: COLORS.navy,
    marginBottom: 2,
  },

  promotionText: {
    fontSize: 6.2,
    color: COLORS.darkGray,
    lineHeight: 1.2,
  },

  /* REMARK */

  remarkBox: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: COLORS.navy,
    paddingVertical: 5,
    paddingHorizontal: 8,
    marginTop: 4,
    borderRadius: 3,
  },

  remarkLabel: {
    fontSize: 7,
    fontWeight: "bold",
    color: COLORS.gold,
    marginRight: 5,
  },

  remarkText: {
    flex: 1,
    fontSize: 7,
    color: COLORS.white,
  },

  /* GRADE LEGEND */

  gradeLegend: {
    backgroundColor: COLORS.paleBlue,
    borderWidth: 1,
    borderColor: COLORS.blueBorder,
    paddingVertical: 4,
    paddingHorizontal: 6,
    marginTop: 4,
    textAlign: "center",
    fontSize: 6,
    color: COLORS.darkGray,
    borderRadius: 3,
  },

  /* CONDUCT AND RANK */

  conductRankBar: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 3,
    paddingVertical: 2,
  },

  conductText: {
    fontSize: 7,
    fontWeight: "bold",
    color: COLORS.darkGray,
  },

  rankBadge: {
    backgroundColor: COLORS.navy,
    borderRadius: 3,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },

  rankText: {
    color: COLORS.white,
    fontSize: 7,
    fontWeight: "bold",
  },

  /* VERTICAL RANK FRACTION */

  rankFraction: {
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 25,
  },

  rankNumerator: {
    fontSize: 8,
    fontWeight: "bold",
    color: COLORS.white,
    textAlign: "center",
    lineHeight: 1,
  },

  rankDivider: {
    width: 22,
    borderTopWidth: 0.8,
    borderTopColor: COLORS.white,
    marginVertical: 2,
  },

  rankDenominator: {
    fontSize: 8,
    fontWeight: "bold",
    color: COLORS.white,
    textAlign: "center",
    lineHeight: 1,
  },

  /* SIGNATURES */

  signatureSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    backgroundColor: COLORS.paleBlue,
    borderWidth: 1,
    borderColor: COLORS.blueBorder,
    borderRadius: 4,
    paddingVertical: 4,
    paddingHorizontal: 35,
    marginTop: 3,
  },

  signatureItem: {
    width: "35%",
    alignItems: "center",
  },

  signatureLabel: {
    fontSize: 6,
    fontWeight: "bold",
    color: COLORS.gray,
    marginBottom: 8,
  },

  signatureLine: {
    width: "100%",
    borderTopWidth: 1,
    borderTopColor: COLORS.navy,
    marginBottom: 2,
  },

  signatureDate: {
    fontSize: 5.5,
    color: COLORS.gray,
  },

  /* ==========================================================================
     BACK PAGE STYLES
  ========================================================================== */

  backPageHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: COLORS.paleBlue,
    borderWidth: 1,
    borderColor: COLORS.blueBorder,
    borderRadius: 7,
    padding: 9,
    marginBottom: 9,
  },

  backPageHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },

  backPageLogo: {
    width: 52,
    height: 47,
    objectFit: "contain",
    marginRight: 10,
  },

  backPageTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: COLORS.navy,
  },

  backPageSubtitle: {
    fontSize: 7.5,
    color: COLORS.gray,
    marginTop: 3,
  },

  backPageTag: {
    backgroundColor: COLORS.navy,
    color: COLORS.white,
    fontSize: 8,
    fontWeight: "bold",
    paddingHorizontal: 11,
    paddingVertical: 6,
    borderRadius: 4,
  },

  backColumns: {
    flexDirection: "row",
    width: "100%",
    alignItems: "flex-start",
  },

  backColumnLeft: {
    width: "49%",
    marginRight: "2%",
  },

  backColumnRight: {
    width: "49%",
  },

  card: {
    backgroundColor: COLORS.white,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 6,
    padding: 9,
    marginBottom: 8,
  },

  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: COLORS.paleBlue,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.blueBorder,
    marginHorizontal: -9,
    marginTop: -9,
    marginBottom: 8,
    paddingHorizontal: 9,
    paddingVertical: 6,
  },

  cardHeaderAccent: {
    width: 4,
    height: 14,
    backgroundColor: COLORS.gold,
    marginRight: 7,
    borderRadius: 2,
  },

  cardTitle: {
    fontSize: 8.5,
    fontWeight: "bold",
    color: COLORS.navy,
  },

  /* SCHOOL PROFILE */

  schoolProfileContent: {
    alignItems: "center",
    justifyContent: "center",
  },

  schoolProfileLogo: {
    width: 54,
    height: 54,
    objectFit: "contain",
    marginBottom: 5,
  },

  schoolProfileName: {
    fontSize: 10,
    fontWeight: "bold",
    color: COLORS.navy,
    textAlign: "center",
    marginBottom: 4,
  },

  schoolProfileLocation: {
    fontSize: 7.5,
    color: COLORS.gray,
    textAlign: "center",
    lineHeight: 1.35,
  },

  /* STUDENT PROFILE */

  studentProfileRow: {
    flexDirection: "row",
    alignItems: "center",
  },

  avatar: {
    width: 65,
    height: 65,
    borderRadius: 5,
    objectFit: "cover",
    marginRight: 11,
    borderWidth: 2,
    borderColor: COLORS.gold,
  },

  studentProfileDetails: {
    flex: 1,
  },

  profileStudentName: {
    fontSize: 11,
    fontWeight: "bold",
    color: COLORS.navy,
    marginBottom: 5,
  },

  profileDetailRow: {
    flexDirection: "row",
    marginBottom: 4,
  },

  profileDetailLabel: {
    width: 75,
    fontSize: 7.5,
    color: COLORS.gray,
  },

  profileDetailValue: {
    flex: 1,
    fontSize: 8,
    fontWeight: "bold",
    color: COLORS.darkGray,
  },

  /* PERFORMANCE CIRCLES */

  averageSummaryRow: {
    flexDirection: "row",
    justifyContent: "space-around",
    alignItems: "center",
    paddingVertical: 3,
  },

  averageSummaryItem: {
    width: 90,
    alignItems: "center",
  },

  averageSummaryCircle: {
    width: 62,
    height: 62,
    borderRadius: 31,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 2,
    borderColor: COLORS.blueText,
    backgroundColor: COLORS.lightBlue,
  },

  averageSummaryCircleOrange: {
    width: 62,
    height: 62,
    borderRadius: 31,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 2,
    borderColor: COLORS.gold,
    backgroundColor: COLORS.lightGold,
  },

  averageSummaryCircleGreen: {
    width: 62,
    height: 62,
    borderRadius: 31,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 2,
    borderColor: COLORS.green,
    backgroundColor: COLORS.lightGreen,
  },

  averageSummaryLabel: {
    fontSize: 7,
    fontWeight: "bold",
    textAlign: "center",
    color: COLORS.gray,
    marginTop: 5,
  },

  averageSummaryValue: {
    fontSize: 11,
    fontWeight: "bold",
  },

  /* PROMOTION OPTIONS */

  promotionOption: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 5,
    borderBottomWidth: 0.5,
    borderBottomColor: COLORS.border,
  },

  promotionLetter: {
    width: 19,
    height: 19,
    borderRadius: 9.5,
    backgroundColor: COLORS.lightGold,
    borderWidth: 1,
    borderColor: COLORS.gold,
    justifyContent: "center",
    alignItems: "center",
    marginRight: 8,
  },

  promotionLetterText: {
    fontSize: 8,
    fontWeight: "bold",
    color: COLORS.navy,
  },

  promotionOptionText: {
    flex: 1,
    fontSize: 8,
    color: COLORS.darkGray,
  },

  promotionLine: {
    width: 110,
    borderBottomWidth: 0.8,
    borderBottomColor: COLORS.navy,
    marginLeft: 5,
  },

  /* BACK PAGE PERIOD AVERAGES */

  backPeriodAverageRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 4,
    borderBottomWidth: 0.5,
    borderBottomColor: COLORS.border,
  },

  periodAverageName: {
    fontSize: 8,
    color: COLORS.darkGray,
  },

  periodAverageValue: {
    fontSize: 8.5,
    fontWeight: "bold",
  },

  /* INFORMATION GRID */

  infoGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
  },

  infoGridItem: {
    width: "50%",
    paddingVertical: 4,
  },

  infoGridLabel: {
    fontSize: 7,
    color: COLORS.gray,
    marginBottom: 2,
  },

  infoGridValue: {
    fontSize: 8.5,
    fontWeight: "bold",
    color: COLORS.darkGray,
  },

  /* PARENT SIGNATURE */

  parentSignatureCard: {
    backgroundColor: COLORS.lightGold,
    borderWidth: 1,
    borderColor: COLORS.gold,
    borderRadius: 6,
    padding: 10,
    alignItems: "center",
  },

  parentSignatureTitle: {
    fontSize: 8.5,
    fontWeight: "bold",
    color: COLORS.navy,
    marginBottom: 17,
  },

  parentSignatureLine: {
    width: "75%",
    borderTopWidth: 1,
    borderTopColor: COLORS.navy,
    marginBottom: 4,
  },

  parentSignatureCaption: {
    fontSize: 7,
    color: COLORS.gray,
  },

  /* BACK FOOTER */

  backFooter: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: COLORS.navy,
    paddingVertical: 7,
    paddingHorizontal: 10,
    borderRadius: 4,
    marginTop: 2,
  },

  backFooterText: {
    color: COLORS.white,
    fontSize: 6.5,
    flex: 1,
    marginRight: 8,
  },

  backFooterHighlight: {
    color: COLORS.gold,
    fontSize: 7,
    fontWeight: "bold",
  },
});

/* ============================================================================
   DYNAMIC SCORE CIRCLE
============================================================================ */

const ScoreCircle = ({
  value,
  size = 22,
  fontSize = 8.5,
}) => {
  const formatted = formatValue(value);

  return (
    <View
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        borderWidth: 1.1,
        borderColor: getScoreBorder(formatted),
        backgroundColor: getScoreBackground(formatted),
        justifyContent: "center",
        alignItems: "center",
        alignSelf: "center",
      }}
    >
      <Text
        style={{
          fontSize,
          fontWeight: "bold",
          textAlign: "center",
          color: getColor(formatted),
        }}
      >
        {formatted}
      </Text>
    </View>
  );
};

/* ============================================================================
   VERTICAL RANK FRACTION COMPONENT
============================================================================ */

const RankFraction = ({
  rank,
  color = COLORS.white,
}) => {
  const rankParts = getRankParts(rank);

  // Display N/A or any non-fraction value normally.
  if (!rankParts) {
    return (
      <Text
        style={{
          fontSize: 7,
          fontWeight: "bold",
          color,
        }}
      >
        {formatValue(rank)}
      </Text>
    );
  }

  return (
    <View
      style={[
        styles.rankFraction,
        {
          minWidth: 25,
        },
      ]}
    >
      {/* NUMERATOR */}

      <Text
        style={[
          styles.rankNumerator,
          { color },
        ]}
      >
        {rankParts.numerator}
      </Text>

      {/* HORIZONTAL DIVIDER */}

      <View
        style={[
          styles.rankDivider,
          { borderTopColor: color },
        ]}
      />

      {/* DENOMINATOR */}

      <Text
        style={[
          styles.rankDenominator,
          { color },
        ]}
      >
        {rankParts.denominator}
      </Text>
    </View>
  );
};

/* ============================================================================
   REPORT CARD COMPONENT
============================================================================ */

const ReportCardPDF = ({
  responses = [],
  selectedGradeClass,
  selectedYear,
  showRemarksInPDF,
}) => {
  return (
    <Document>

      {responses.map((res, idx) => {
        if (!res?.data?.period_grades) {
          return null;
        }

        const data = res.data;

        /* ================================================================
           DIVISION DETECTION
        ================================================================ */

        const gradeClassName = data.grade_class_name || "";
        const normalizedClass = gradeClassName.toLowerCase();

        const division =
          normalizedClass.includes("nursery") ||
          normalizedClass.includes("kindergarten") ||
          normalizedClass.includes("ece")
            ? "ECE"
            : "Elementary";

        /* ================================================================
           SUBJECT ORDER
        ================================================================ */

        const order = SUBJECT_ORDER[division] || [];

        const dataSubjects = [
          ...new Set(
            data.period_grades
              .map((g) => g.subject_name?.trim())
              .filter(Boolean)
          ),
        ];

        const extraSubjects = dataSubjects.filter(
          (subject) =>
            !order.some(
              (orderedSubject) =>
                orderedSubject.toLowerCase() === subject.toLowerCase()
            )
        );

        const subjects = [
          ...order.filter((subject) =>
            dataSubjects.some(
              (dataSubject) =>
                dataSubject.toLowerCase() === subject.toLowerCase()
            )
          ),
          ...extraSubjects,
        ];

        /* ================================================================
           DYNAMIC FRONT-PAGE TABLE SIZING

           More subjects = shorter rows and smaller circles.
           This keeps all subjects on the front page.
        ================================================================ */

        const subjectCount = subjects.length;

        let rowHeight = 24;
        let circleSize = 21;
        let circleFontSize = 8.5;
        let subjectFontSize = 8.5;
        let headerHeight = 23;

        if (subjectCount >= 13) {
          rowHeight = 21;
          circleSize = 18;
          circleFontSize = 7.5;
          subjectFontSize = 7.8;
          headerHeight = 21;
        }

        if (subjectCount >= 14) {
          rowHeight = 19;
          circleSize = 16;
          circleFontSize = 6.8;
          subjectFontSize = 7.2;
          headerHeight = 20;
        }

        if (subjectCount >= 16) {
          rowHeight = 17;
          circleSize = 14;
          circleFontSize = 6;
          subjectFontSize = 6.5;
          headerHeight = 18;
        }

        /* ================================================================
           SUBJECT AVERAGES
        ================================================================ */

        const getSubjectAverage = (subject, field) => {
          return (
            data.subjects_semester_averages?.find(
              (item) =>
                item.subject_name?.toLowerCase() === subject.toLowerCase()
            )?.[field] ?? "N/A"
          );
        };

        /* ================================================================
           SUBJECT GRADES
        ================================================================ */

        const getSubjectGrades = (subject) => {
          return data.period_grades.filter(
            (grade) =>
              grade.subject_name?.toLowerCase() === subject.toLowerCase()
          );
        };

        /* ================================================================
           PERIOD SCORE
        ================================================================ */

        const getPeriodScore = (subject, period) => {
          const grade = getSubjectGrades(subject).find(
            (item) => item.period_name === period
          );

          return formatValue(grade?.formatted_final_score);
        };

        /* ================================================================
           STUDENT PHOTO
        ================================================================ */

        const studentPhoto =
          data.student_admission_image || studentAvatar;

        /* ================================================================
           DYNAMIC CELL STYLES
        ================================================================ */

        const subjectRowStyle = {
          minHeight: rowHeight,
          height: rowHeight,
        };

        const subjectCellStyle = {
          minHeight: rowHeight,
          height: rowHeight,
          fontSize: subjectFontSize,
        };

        const scoreCellStyle = {
          minHeight: rowHeight,
          height: rowHeight,
          paddingVertical: 1,
        };

        const headerRowStyle = {
          minHeight: headerHeight,
          height: headerHeight,
        };

        return (
          <React.Fragment key={idx}>

            {/* ============================================================
                FRONT PAGE
            ============================================================ */}

            <Page
              size="A4"
              orientation="landscape"
              style={styles.page}
              wrap={false}
            >

              <View style={styles.topAccentBar} />

              {/* SCHOOL HEADER */}

              <View style={styles.header}>

                <View style={styles.logoSection}>
                  <Image src={logo} style={styles.logo} />

                  <View style={styles.schoolIdentity}>
                    <Text style={styles.schoolName}>
                      Zion Praise Academy
                    </Text>

                    <Text style={styles.schoolSubtitle}>
                      CRM SCHOOL SYSTEM • ACADEMIC EXCELLENCE • CHARACTER • FAITH
                    </Text>

                    <Text style={styles.schoolLocation}>
                      1st Cowfield Junction, Duport Road, Paynesville, Monrovia, Liberia
                    </Text>
                  </View>
                </View>

                <View style={styles.reportTitleSection}>
                  <Text style={styles.reportCardTitle}>
                    STUDENT REPORT CARD
                  </Text>

                  <Text style={styles.reportCardSubtitle}>
                    Official Academic Performance Record
                  </Text>
                </View>

                <Image
                  src={studentPhoto}
                  style={styles.frontAvatar}
                />

              </View>

              {/* STUDENT INFORMATION */}

              <View style={styles.studentInfoBar}>

                <View style={styles.studentInfoItem}>
                  <Text style={styles.studentInfoLabel}>
                    STUDENT NAME
                  </Text>

                  <Text style={styles.studentInfoValue}>
                    {data.student_name || "N/A"}
                  </Text>
                </View>

                <View style={styles.studentInfoItem}>
                  <Text style={styles.studentInfoLabel}>
                    GRADE CLASS
                  </Text>

                  <Text style={styles.studentInfoValue}>
                    {data.grade_class_name || "N/A"}
                  </Text>
                </View>

                <View style={styles.studentInfoItem}>
                  <Text style={styles.studentInfoLabel}>
                    ACADEMIC YEAR
                  </Text>

                  <Text style={styles.studentInfoValue}>
                    {selectedYear?.name || "N/A"}
                  </Text>
                </View>

                <View style={styles.studentInfoItem}>
                  <Text style={styles.studentInfoLabel}>
                    DIVISION
                  </Text>

                  <Text style={styles.studentInfoValue}>
                    {division}
                  </Text>
                </View>

              </View>

              {/* ACADEMIC SECTION */}

              <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>
                  ACADEMIC PERFORMANCE SUMMARY
                </Text>

                <Text style={styles.sectionTag}>
                  ANNUAL ASSESSMENT RECORD
                </Text>
              </View>

              {/* ==========================================================
                  ACADEMIC TABLE
              ========================================================== */}

              <View style={styles.academicTableWrapper}>

                {/* SEMESTER ONE */}

                <View style={styles.academicHalfTable}>

                  <View
                    style={[
                      styles.academicHeaderRow,
                      headerRowStyle,
                    ]}
                  >

                    <Text
                      style={[
                        styles.academicSubjectHeaderCell,
                        { height: headerHeight },
                      ]}
                    >
                      SUBJECTS
                    </Text>

                    {sem1Periods.map((period) => (
                      <Text
                        key={period}
                        style={[
                          styles.academicScoreHeaderCell,
                          { height: headerHeight },
                        ]}
                      >
                        {period}
                      </Text>
                    ))}

                    <Text
                      style={[
                        styles.academicScoreHeaderCell,
                        {
                          height: headerHeight,
                          backgroundColor: COLORS.white,
                          color: COLORS.blueText,
                        },
                      ]}
                    >
                      SA1
                    </Text>

                  </View>

                  {subjects.map((subject) => {
                    const sem1Avg = getSubjectAverage(
                      subject,
                      "semester_1_avg"
                    );

                    return (
                      <View
                        key={`sem1-${subject}`}
                        style={[
                          styles.academicBodyRow,
                          subjectRowStyle,
                        ]}
                      >

                        <View
                          style={[
                            styles.academicSubjectCell,
                            subjectCellStyle,
                          ]}
                        >
                          <Text
                            style={{
                              fontSize: subjectFontSize,
                              fontWeight: "bold",
                              color: COLORS.darkGray,
                              textAlign: "center",
                            }}
                          >
                            {subject}
                          </Text>
                        </View>

                        {sem1Periods.map((period) => (
                          <View
                            key={`${subject}-${period}`}
                            style={[
                              styles.academicScoreCell,
                              scoreCellStyle,
                            ]}
                          >
                            <ScoreCircle
                              value={getPeriodScore(subject, period)}
                              size={circleSize}
                              fontSize={circleFontSize}
                            />
                          </View>
                        ))}

                        <View
                          style={[
                            styles.academicScoreCell,
                            styles.semesterAverageCell,
                            scoreCellStyle,
                          ]}
                        >
                          <ScoreCircle
                            value={sem1Avg}
                            size={circleSize}
                            fontSize={circleFontSize}
                          />
                        </View>

                      </View>
                    );
                  })}

                </View>

                {/* SEMESTER TWO */}

                <View
                  style={[
                    styles.academicHalfTable,
                    styles.academicHalfTableRight,
                  ]}
                >

                  <View
                    style={[
                      styles.academicHeaderRow,
                      headerRowStyle,
                    ]}
                  >

                    {sem2Periods.map((period) => (
                      <Text
                        key={period}
                        style={[
                          styles.academicScoreHeaderRight,
                          { height: headerHeight },
                        ]}
                      >
                        {period}
                      </Text>
                    ))}

                    <Text
                      style={[
                        styles.academicScoreHeaderRight,
                        {
                          height: headerHeight,
                          color: COLORS.blueText,
                        },
                      ]}
                    >
                      SA2
                    </Text>

                    <Text
                      style={[
                        styles.academicScoreHeaderRight,
                        {
                          height: headerHeight,
                          color: "#92722A",
                        },
                      ]}
                    >
                      YEARLY
                    </Text>

                  </View>

                  {subjects.map((subject) => {
                    const sem2Avg = getSubjectAverage(
                      subject,
                      "semester_2_avg"
                    );

                    const yearlyAvg = getSubjectAverage(
                      subject,
                      "yearly_avg"
                    );

                    return (
                      <View
                        key={`sem2-${subject}`}
                        style={[
                          styles.academicBodyRow,
                          subjectRowStyle,
                        ]}
                      >

                        {sem2Periods.map((period) => (
                          <View
                            key={`${subject}-${period}`}
                            style={[
                              styles.academicScoreCellRight,
                              scoreCellStyle,
                            ]}
                          >
                            <ScoreCircle
                              value={getPeriodScore(subject, period)}
                              size={circleSize}
                              fontSize={circleFontSize}
                            />
                          </View>
                        ))}

                        <View
                          style={[
                            styles.academicScoreCellRight,
                            styles.semesterAverageCell,
                            scoreCellStyle,
                          ]}
                        >
                          <ScoreCircle
                            value={sem2Avg}
                            size={circleSize}
                            fontSize={circleFontSize}
                          />
                        </View>

                        <View
                          style={[
                            styles.academicScoreCellRight,
                            styles.yearlyAverageCell,
                            scoreCellStyle,
                          ]}
                        >
                          <ScoreCircle
                            value={yearlyAvg}
                            size={circleSize}
                            fontSize={circleFontSize}
                          />
                        </View>

                      </View>
                    );
                  })}

                </View>

              </View>

              {/* ==========================================================
                  PERIOD AVERAGES
              ========================================================== */}

              <View style={styles.academicTableWrapper}>

                {/* SEMESTER ONE AVERAGES */}

                <View style={styles.academicHalfTable}>

                  <View
                    style={[
                      styles.periodAverageRow,
                      { minHeight: 24 },
                    ]}
                  >

                    <View
                      style={[
                        styles.academicSubjectCell,
                        {
                          minHeight: 24,
                          backgroundColor: COLORS.lightGray,
                        },
                      ]}
                    >
                      <Text
                        style={{
                          fontSize: 6.5,
                          fontWeight: "bold",
                          color: COLORS.navy,
                          textAlign: "center",
                        }}
                      >
                        PERIOD AVERAGES
                      </Text>
                    </View>

                    {sem1Periods.map((period) => {
                      const avg =
                        data.period_average?.[period] ?? "N/A";

                      return (
                        <View
                          key={period}
                          style={[
                            styles.academicScoreCell,
                            {
                              minHeight: 24,
                              backgroundColor: COLORS.lightGray,
                            },
                          ]}
                        >
                          <ScoreCircle
                            value={avg}
                            size={17}
                            fontSize={6.5}
                          />
                        </View>
                      );
                    })}

                    <View
                      style={[
                        styles.academicScoreCell,
                        {
                          minHeight: 24,
                          backgroundColor: COLORS.lightBlue,
                        },
                      ]}
                    >
                      <Text style={styles.averageLabel}>SA1</Text>

                      <Text
                        style={[
                          styles.averageText,
                          { color: getColor(data.semester_1_avg) },
                        ]}
                      >
                        {formatValue(data.semester_1_avg)}
                      </Text>
                    </View>

                  </View>

                </View>

                {/* SEMESTER TWO AVERAGES */}

                <View
                  style={[
                    styles.academicHalfTable,
                    styles.academicHalfTableRight,
                  ]}
                >

                  <View
                    style={[
                      styles.periodAverageRow,
                      { minHeight: 24 },
                    ]}
                  >

                    {sem2Periods.map((period) => {
                      const avg =
                        data.period_average?.[period] ?? "N/A";

                      return (
                        <View
                          key={period}
                          style={[
                            styles.academicScoreCellRight,
                            {
                              minHeight: 24,
                              backgroundColor: COLORS.lightGray,
                            },
                          ]}
                        >
                          <ScoreCircle
                            value={avg}
                            size={17}
                            fontSize={6.5}
                          />
                        </View>
                      );
                    })}

                    <View
                      style={[
                        styles.academicScoreCellRight,
                        {
                          minHeight: 24,
                          backgroundColor: COLORS.lightBlue,
                        },
                      ]}
                    >
                      <Text style={styles.averageLabel}>SA2</Text>

                      <Text
                        style={[
                          styles.averageText,
                          { color: getColor(data.semester_2_avg) },
                        ]}
                      >
                        {formatValue(data.semester_2_avg)}
                      </Text>
                    </View>

                    <View
                      style={[
                        styles.academicScoreCellRight,
                        {
                          minHeight: 24,
                          backgroundColor: COLORS.lightGold,
                        },
                      ]}
                    >
                      <Text style={styles.averageLabel}>YEARLY</Text>

                      <Text
                        style={[
                          styles.averageText,
                          { color: getColor(data.yearly_average) },
                        ]}
                      >
                        {formatValue(data.yearly_average)}
                      </Text>
                    </View>

                  </View>

                </View>

              </View>

              {/* PERFORMANCE SUMMARY */}

              <View style={styles.performanceSummary}>

                <View style={styles.summaryCard}>
                  <Text style={styles.summaryLabel}>
                    SEMESTER 1 AVERAGE
                  </Text>

                  <Text
                    style={[
                      styles.summaryValue,
                      { color: getColor(data.semester_1_avg) },
                    ]}
                  >
                    {formatValue(data.semester_1_avg)}
                  </Text>
                </View>

                <View style={styles.summaryCard}>
                  <Text style={styles.summaryLabel}>
                    SEMESTER 2 AVERAGE
                  </Text>

                  <Text
                    style={[
                      styles.summaryValue,
                      { color: getColor(data.semester_2_avg) },
                    ]}
                  >
                    {formatValue(data.semester_2_avg)}
                  </Text>
                </View>

                <View style={styles.summaryCardLast}>
                  <Text style={styles.summaryLabel}>
                    FINAL YEARLY AVERAGE
                  </Text>

                  <Text
                    style={[
                      styles.summaryValue,
                      { color: getColor(data.yearly_average) },
                    ]}
                  >
                    {formatValue(data.yearly_average)}
                  </Text>
                </View>

              </View>

              {/* PROMOTION POLICY */}

              <View style={styles.promotionBox}>
                <Text style={styles.promotionTitle}>
                  PROMOTION POLICY
                </Text>

                <Text style={styles.promotionText}>
                  A student who fails two major subjects may not be eligible
                  for promotion. A student who fails one major subject may
                  be required to attend vacation school or complete an
                  approved academic intervention program.
                </Text>
              </View>

              {/* FINAL REMARK */}

              <View style={styles.remarkBox}>
                <Text style={styles.remarkLabel}>
                  FINAL REMARK:
                </Text>

                <Text style={styles.remarkText}>
                  {showRemarksInPDF
                    ? data.remark || "No remark provided."
                    : "Year-End Remark will appear here after review."}
                </Text>
              </View>

              {/* GRADING SCALE */}

              <Text style={styles.gradeLegend}>
                GRADING SCALE: 60–69 FAIL | 70–79 FAIR | 80–89 GOOD |
                90–95 VERY GOOD | 96–100 EXCELLENT
              </Text>

              {/* CONDUCT AND RANK */}

              <View style={styles.conductRankBar}>
                <Text style={styles.conductText}>
                  YEARLY CONDUCT: {data.yearly_conduct || "N/A"}
                </Text>

                {/* CLASS RANK - VERTICAL FRACTION */}

                <View
                  style={[
                    styles.rankBadge,
                    {
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 85,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.rankText,
                      { marginRight: 6 },
                    ]}
                  >
                    CLASS RANK:
                  </Text>

                  <RankFraction rank={data.rank} />
                </View>
              </View>

              {/* SIGNATURES */}

              <View style={styles.signatureSection}>

                <View style={styles.signatureItem}>
                  <Text style={styles.signatureLabel}>
                    PRINCIPAL'S SIGNATURE
                  </Text>

                  <View style={styles.signatureLine} />

                  <Text style={styles.signatureDate}>
                    Date: ____________________
                  </Text>
                </View>

                <View style={styles.signatureItem}>
                  <Text style={styles.signatureLabel}>
                    CLASS SPONSOR'S SIGNATURE
                  </Text>

                  <View style={styles.signatureLine} />

                  <Text style={styles.signatureDate}>
                    Date: ____________________
                  </Text>
                </View>

              </View>

            </Page>

            {/* ============================================================
                BACK PAGE
                Exactly one back page per student.
            ============================================================ */}

            <Page
              size="A4"
              orientation="landscape"
              style={styles.backPage}
              wrap={false}
            >

              {/* BACK HEADER */}

              <View style={styles.backPageHeader}>

                <View style={styles.backPageHeaderLeft}>
                  <Image src={logo} style={styles.backPageLogo} />

                  <View>
                    <Text style={styles.backPageTitle}>
                      STUDENT ACADEMIC PROFILE
                    </Text>

                    <Text style={styles.backPageSubtitle}>
                      Official School Record • Student Development • Promotion
                    </Text>
                  </View>
                </View>

                <Text style={styles.backPageTag}>
                  BACK PAGE
                </Text>

              </View>

              {/* TWO COLUMNS */}

              <View style={styles.backColumns}>

                {/* LEFT COLUMN */}

                <View style={styles.backColumnLeft}>

                  {/* SCHOOL INFORMATION */}

                  <View style={styles.card}>

                    <View style={styles.cardHeader}>
                      <View style={styles.cardHeaderAccent} />

                      <Text style={styles.cardTitle}>
                        SCHOOL INFORMATION
                      </Text>
                    </View>

                    <View style={styles.schoolProfileContent}>

                      <Image
                        src={logo}
                        style={styles.schoolProfileLogo}
                      />

                      <Text style={styles.schoolProfileName}>
                        Zion Praise Academy - CRM School System
                      </Text>

                      <Text style={styles.schoolProfileLocation}>
                        Cowfield 1st Junction, Duport Road,
                        Paynesville, Monrovia, Liberia
                      </Text>

                      <View style={styles.infoGrid}>

                        <View style={styles.infoGridItem}>
                          <Text style={styles.infoGridLabel}>
                            SCHOOL YEAR
                          </Text>

                          <Text style={styles.infoGridValue}>
                            {selectedYear?.name || "N/A"}
                          </Text>
                        </View>

                        <View style={styles.infoGridItem}>
                          <Text style={styles.infoGridLabel}>
                            DIVISION
                          </Text>

                          <Text style={styles.infoGridValue}>
                            {division}
                          </Text>
                        </View>

                      </View>

                    </View>

                  </View>

                  {/* STUDENT PROFILE */}

                  <View style={styles.card}>

                    <View style={styles.cardHeader}>
                      <View style={styles.cardHeaderAccent} />

                      <Text style={styles.cardTitle}>
                        STUDENT PROFILE
                      </Text>
                    </View>

                    <View style={styles.studentProfileRow}>

                      <Image
                        src={studentPhoto}
                        style={styles.avatar}
                      />

                      <View style={styles.studentProfileDetails}>

                        <Text style={styles.profileStudentName}>
                          {data.student_name || "N/A"}
                        </Text>

                        <View style={styles.profileDetailRow}>
                          <Text style={styles.profileDetailLabel}>
                            Class:
                          </Text>

                          <Text style={styles.profileDetailValue}>
                            {data.grade_class_name || "N/A"}
                          </Text>
                        </View>

                        <View style={styles.profileDetailRow}>
                          <Text style={styles.profileDetailLabel}>
                            Academic Year:
                          </Text>

                          <Text style={styles.profileDetailValue}>
                            {selectedYear?.name || "N/A"}
                          </Text>
                        </View>

                        {/* RANK - VERTICAL FRACTION */}

                        <View
                          style={[
                            styles.profileDetailRow,
                            { alignItems: "center" },
                          ]}
                        >
                          <Text style={styles.profileDetailLabel}>
                            Rank:
                          </Text>

                          <View style={{ flex: 1 }}>
                            <RankFraction
                              rank={data.rank}
                              color={COLORS.navy}
                            />
                          </View>
                        </View>

                      </View>

                    </View>

                  </View>

                  {/* PERFORMANCE OVERVIEW */}

                  <View style={styles.card}>

                    <View style={styles.cardHeader}>
                      <View style={styles.cardHeaderAccent} />

                      <Text style={styles.cardTitle}>
                        PERFORMANCE OVERVIEW
                      </Text>
                    </View>

                    <View style={styles.averageSummaryRow}>

                      <View style={styles.averageSummaryItem}>
                        <View style={styles.averageSummaryCircle}>
                          <Text
                            style={[
                              styles.averageSummaryValue,
                              { color: getColor(data.semester_1_avg) },
                            ]}
                          >
                            {formatValue(data.semester_1_avg)}
                          </Text>
                        </View>

                        <Text style={styles.averageSummaryLabel}>
                          SEMESTER 1
                        </Text>
                      </View>

                      <View style={styles.averageSummaryItem}>
                        <View style={styles.averageSummaryCircleOrange}>
                          <Text
                            style={[
                              styles.averageSummaryValue,
                              { color: getColor(data.semester_2_avg) },
                            ]}
                          >
                            {formatValue(data.semester_2_avg)}
                          </Text>
                        </View>

                        <Text style={styles.averageSummaryLabel}>
                          SEMESTER 2
                        </Text>
                      </View>

                      <View style={styles.averageSummaryItem}>
                        <View style={styles.averageSummaryCircleGreen}>
                          <Text
                            style={[
                              styles.averageSummaryValue,
                              { color: getColor(data.yearly_average) },
                            ]}
                          >
                            {formatValue(data.yearly_average)}
                          </Text>
                        </View>

                        <Text style={styles.averageSummaryLabel}>
                          FINAL YEARLY
                        </Text>
                      </View>

                    </View>

                  </View>

                  {/* STUDENT DEVELOPMENT */}

                  <View style={styles.card}>

                    <View style={styles.cardHeader}>
                      <View style={styles.cardHeaderAccent} />

                      <Text style={styles.cardTitle}>
                        STUDENT DEVELOPMENT
                      </Text>
                    </View>

                    <View style={styles.infoGrid}>

                      <View style={styles.infoGridItem}>
                        <Text style={styles.infoGridLabel}>
                          YEARLY CONDUCT
                        </Text>

                        <Text style={styles.infoGridValue}>
                          {data.yearly_conduct || "N/A"}
                        </Text>
                      </View>

                      <View style={styles.infoGridItem}>
                        <Text style={styles.infoGridLabel}>
                          CLASS RANK
                        </Text>

                        <View style={{ alignItems: "flex-start" }}>
                          <RankFraction
                            rank={data.rank}
                            color={COLORS.navy}
                          />
                        </View>
                      </View>

                    </View>

                  </View>

                </View>

                {/* RIGHT COLUMN */}

                <View style={styles.backColumnRight}>

                  {/* PROMOTION DECISION */}

                  <View style={styles.card}>

                    <View style={styles.cardHeader}>
                      <View style={styles.cardHeaderAccent} />

                      <Text style={styles.cardTitle}>
                        PROMOTION DECISION
                      </Text>
                    </View>

                    {[
                      ["A", "Promoted to:"],
                      ["B", "Condition in:"],
                      ["C", "Fail / Repeat Grade Class"],
                      ["D", "Never to Return"],
                    ].map(([letter, label]) => (
                      <View
                        key={letter}
                        style={styles.promotionOption}
                      >
                        <View style={styles.promotionLetter}>
                          <Text style={styles.promotionLetterText}>
                            {letter}
                          </Text>
                        </View>

                        <Text style={styles.promotionOptionText}>
                          {label}
                        </Text>

                        {letter !== "C" && letter !== "D" && (
                          <View style={styles.promotionLine} />
                        )}
                      </View>
                    ))}

                  </View>

                  {/* PERIOD AVERAGES */}

                  <View style={styles.card}>

                    <View style={styles.cardHeader}>
                      <View style={styles.cardHeaderAccent} />

                      <Text style={styles.cardTitle}>
                        PERIOD AVERAGES
                      </Text>
                    </View>

                    {allPeriods.map((period) => (
                      <View
                        key={period}
                        style={styles.backPeriodAverageRow}
                      >
                        <Text style={styles.periodAverageName}>
                          {period}
                        </Text>

                        <Text
                          style={[
                            styles.periodAverageValue,
                            {
                              color: getColor(
                                data.period_average?.[period]
                              ),
                            },
                          ]}
                        >
                          {formatValue(
                            data.period_average?.[period]
                          )}
                        </Text>
                      </View>
                    ))}

                  </View>

                  {/* FINAL ACADEMIC SUMMARY */}

                  <View style={styles.card}>

                    <View style={styles.cardHeader}>
                      <View style={styles.cardHeaderAccent} />

                      <Text style={styles.cardTitle}>
                        FINAL ACADEMIC SUMMARY
                      </Text>
                    </View>

                    <View style={styles.infoGrid}>

                      <View style={styles.infoGridItem}>
                        <Text style={styles.infoGridLabel}>
                          SEMESTER 1 AVERAGE
                        </Text>

                        <Text
                          style={[
                            styles.infoGridValue,
                            { color: getColor(data.semester_1_avg) },
                          ]}
                        >
                          {formatValue(data.semester_1_avg)}
                        </Text>
                      </View>

                      <View style={styles.infoGridItem}>
                        <Text style={styles.infoGridLabel}>
                          SEMESTER 2 AVERAGE
                        </Text>

                        <Text
                          style={[
                            styles.infoGridValue,
                            { color: getColor(data.semester_2_avg) },
                          ]}
                        >
                          {formatValue(data.semester_2_avg)}
                        </Text>
                      </View>

                      <View style={styles.infoGridItem}>
                        <Text style={styles.infoGridLabel}>
                          YEARLY AVERAGE
                        </Text>

                        <Text
                          style={[
                            styles.infoGridValue,
                            { color: getColor(data.yearly_average) },
                          ]}
                        >
                          {formatValue(data.yearly_average)}
                        </Text>
                      </View>

                      <View style={styles.infoGridItem}>
                        <Text style={styles.infoGridLabel}>
                          CLASS RANK
                        </Text>

                        <View style={{ alignItems: "flex-start" }}>
                          <RankFraction
                            rank={data.rank}
                            color={COLORS.navy}
                          />
                        </View>
                      </View>

                    </View>

                  </View>

                  {/* PARENT SIGNATURE */}

                  <View style={styles.parentSignatureCard}>

                    <Text style={styles.parentSignatureTitle}>
                      PARENT / GUARDIAN SIGNATURE
                    </Text>

                    <View style={styles.parentSignatureLine} />

                    <Text style={styles.parentSignatureCaption}>
                      Signature and Date
                    </Text>

                  </View>

                </View>

              </View>

              {/* BACK FOOTER */}

              <View style={styles.backFooter}>

                <Text style={styles.backFooterText}>
                  This report card is an official academic record of the school.
                  Design by: Polu Integrated Technology - School Management System.
                </Text>

                <Text style={styles.backFooterHighlight}>
                  EDUCATION • CHARACTER • EXCELLENCE
                </Text>

              </View>

            </Page>

          </React.Fragment>
        );
      })}

    </Document>
  );
};

export default ReportCardPDF;




// import React from "react";

// import {
//   Document,
//   Page,
//   Text,
//   View,
//   Image,
//   StyleSheet,
// } from "@react-pdf/renderer";

// import logo from "./zpa.png";
// import studentAvatar from "./goldavartar.png";

// /* ============================================================================
//    PERIOD CONSTANTS
// ============================================================================ */

// const sem1Periods = [
//   "Period 1",
//   "Period 2",
//   "Period 3",
//   "Exam",
// ];

// const sem2Periods = [
//   "Period 4",
//   "Period 5",
//   "Period 6",
//   "Exam2",
// ];

// const allPeriods = [...sem1Periods, ...sem2Periods];

// /* ============================================================================
//    SUBJECT ORDERING
// ============================================================================ */

// const SUBJECT_ORDER = {
//   ECE: [
//     "Health Science",
//     "History",
//     "English Writing",
//     "English Reciting",
//     "Mathematic Reciting",
//     "Mathematic Writing",
//     "Reading",
//     "Social Studies",
//     "Phonics",
//     "Bible/RME",
//     "Spelling",
//     "Arts/Drawing",
//     "General Science",
//   ],

//   Elementary: [
//     "Math",
//     "English",
//     "Reading",
//     "Writing",
//     "Phonics",
//     "Spelling",
//     "General Science",
//     "Social Studies",
//     "Bible/RME",
//     "Physical Education",
//     "Arts/Drawing",
//     "Health Science",
//   ],
// };

// /* ============================================================================
//    COLORS
// ============================================================================ */

// const COLORS = {
//   navy: "#092D50",
//   deepNavy: "#061F38",
//   blue: "#174F7D",
//   blueText: "#15558A",
//   blueBorder: "#B8D2E5",
//   lightBlue: "#EDF5FB",
//   paleBlue: "#F5F9FC",

//   red: "#C62828",
//   lightRed: "#FCEDEE",

//   gold: "#D5A83B",
//   lightGold: "#FFF8E6",

//   green: "#39834A",
//   lightGreen: "#EDF7EF",

//   white: "#FFFFFF",
//   black: "#17212B",

//   gray: "#687787",
//   darkGray: "#344454",
//   mediumGray: "#A3AFBA",
//   lightGray: "#F3F6F8",
//   border: "#D5DEE6",
// };

// /* ============================================================================
//    SCORE COLORS
// ============================================================================ */

// const getColor = (value) => {
//   if (
//     value === "N/A" ||
//     value === undefined ||
//     value === null ||
//     value === ""
//   ) {
//     return COLORS.mediumGray;
//   }

//   const textValue = String(value).trim().toUpperCase();

//   if (textValue === "F") {
//     return COLORS.red;
//   }

//   if (["A", "B", "C", "D", "E"].includes(textValue)) {
//     return COLORS.blueText;
//   }

//   const numericValue = Number(value);

//   if (!Number.isNaN(numericValue)) {
//     return numericValue < 70
//       ? COLORS.red
//       : COLORS.blueText;
//   }

//   return COLORS.blueText;
// };

// const getScoreBackground = (value) => {
//   if (
//     value === "N/A" ||
//     value === undefined ||
//     value === null ||
//     value === ""
//   ) {
//     return COLORS.lightGray;
//   }

//   const numericValue = Number(value);

//   if (!Number.isNaN(numericValue) && numericValue < 70) {
//     return COLORS.lightRed;
//   }

//   if (!Number.isNaN(numericValue) && numericValue >= 90) {
//     return COLORS.lightGreen;
//   }

//   return COLORS.lightBlue;
// };

// const getScoreBorder = (value) => {
//   if (
//     value === "N/A" ||
//     value === undefined ||
//     value === null ||
//     value === ""
//   ) {
//     return COLORS.border;
//   }

//   const numericValue = Number(value);

//   if (!Number.isNaN(numericValue) && numericValue < 70) {
//     return COLORS.red;
//   }

//   return COLORS.blueText;
// };

// /* ============================================================================
//    FORMATTER
// ============================================================================ */

// const formatValue = (value) => {
//   if (
//     value === undefined ||
//     value === null ||
//     value === ""
//   ) {
//     return "N/A";
//   }

//   return String(value);
// };

// /* ============================================================================
//    FRONT PAGE STYLES
// ============================================================================ */

// const styles = StyleSheet.create({

//   page: {
//     paddingTop: 10,
//     paddingBottom: 9,
//     paddingHorizontal: 17,
//     backgroundColor: COLORS.white,
//     fontFamily: "Helvetica",
//     fontSize: 9,
//     color: COLORS.black,
//   },

//   backPage: {
//     paddingTop: 17,
//     paddingBottom: 15,
//     paddingHorizontal: 22,
//     backgroundColor: COLORS.white,
//     fontFamily: "Helvetica",
//     fontSize: 9,
//     color: COLORS.black,
//   },

//   topAccentBar: {
//     height: 4,
//     width: "100%",
//     backgroundColor: COLORS.gold,
//     marginBottom: 5,
//     borderRadius: 2,
//   },

//   /* FRONT HEADER */

//   header: {
//     flexDirection: "row",
//     alignItems: "center",
//     justifyContent: "space-between",
//     backgroundColor: COLORS.paleBlue,
//     borderWidth: 1,
//     borderColor: COLORS.blueBorder,
//     borderRadius: 6,
//     paddingVertical: 5,
//     paddingHorizontal: 9,
//     minHeight: 57,
//   },

//   logoSection: {
//     flexDirection: "row",
//     alignItems: "center",
//     flex: 1,
//   },

//   logo: {
//     width: 46,
//     height: 45,
//     objectFit: "contain",
//     marginRight: 8,
//   },

//   schoolIdentity: {
//     flex: 1,
//   },

//   schoolName: {
//     fontSize: 13,
//     fontWeight: "bold",
//     color: COLORS.navy,
//     marginBottom: 2,
//   },

//   schoolSubtitle: {
//     fontSize: 6.5,
//     fontWeight: "bold",
//     color: COLORS.blueText,
//     marginBottom: 2,
//   },

//   schoolLocation: {
//     fontSize: 6.5,
//     color: COLORS.gray,
//   },

//   reportTitleSection: {
//     alignItems: "flex-end",
//     marginLeft: 5,
//     marginRight: 10,
//   },

//   reportCardTitle: {
//     fontSize: 13,
//     fontWeight: "bold",
//     color: COLORS.navy,
//     textAlign: "right",
//     marginBottom: 2,
//   },

//   reportCardSubtitle: {
//     fontSize: 6.5,
//     color: COLORS.gray,
//     textAlign: "right",
//   },

//   frontAvatar: {
//     width: 44,
//     height: 48,
//     objectFit: "cover",
//     borderRadius: 4,
//     borderWidth: 1.5,
//     borderColor: COLORS.gold,
//   },

//   /* STUDENT INFORMATION */

//   studentInfoBar: {
//     flexDirection: "row",
//     alignItems: "center",
//     backgroundColor: COLORS.lightGray,
//     borderLeftWidth: 3,
//     borderLeftColor: COLORS.gold,
//     borderRadius: 4,
//     paddingVertical: 5,
//     paddingHorizontal: 8,
//     marginTop: 5,
//     marginBottom: 4,
//   },

//   studentInfoItem: {
//     flex: 1,
//     paddingRight: 4,
//   },

//   studentInfoLabel: {
//     fontSize: 6,
//     color: COLORS.gray,
//     fontWeight: "bold",
//     marginBottom: 2,
//   },

//   studentInfoValue: {
//     fontSize: 8,
//     fontWeight: "bold",
//     color: COLORS.navy,
//   },

//   /* SECTION HEADER */

//   sectionHeader: {
//     flexDirection: "row",
//     alignItems: "center",
//     justifyContent: "space-between",
//     backgroundColor: COLORS.navy,
//     borderRadius: 3,
//     paddingVertical: 4,
//     paddingHorizontal: 8,
//     marginTop: 3,
//     marginBottom: 4,
//   },

//   sectionTitle: {
//     fontSize: 8,
//     fontWeight: "bold",
//     color: COLORS.white,
//   },

//   sectionTag: {
//     fontSize: 6,
//     fontWeight: "bold",
//     color: COLORS.gold,
//   },

//   /* ACADEMIC TABLE */

//   academicTableWrapper: {
//     flexDirection: "row",
//     width: "100%",
//     borderWidth: 1,
//     borderColor: COLORS.border,
//     borderRadius: 4,
//     overflow: "hidden",
//   },

//   academicHalfTable: {
//     width: "50%",
//   },

//   academicHalfTableRight: {
//     width: "50%",
//     borderLeftWidth: 1.5,
//     borderLeftColor: COLORS.white,
//   },

//   academicHeaderRow: {
//     flexDirection: "row",
//     minHeight: 23,
//     alignItems: "stretch",
//     backgroundColor: COLORS.white,
//     borderBottomWidth: 1,
//     borderBottomColor: COLORS.navy,
//   },

//   academicBodyRow: {
//     flexDirection: "row",
//     alignItems: "center",
//     backgroundColor: COLORS.white,
//   },

//   /* SUBJECT HEADER - NO DARK BACKGROUND */

//   academicSubjectHeaderCell: {
//     width: "37.5%",
//     justifyContent: "center",
//     alignItems: "center",
//     textAlign: "center",
//     paddingHorizontal: 2,
//     borderRightWidth: 0.6,
//     borderColor: COLORS.border,
//     fontSize: 7.5,
//     fontWeight: "bold",
//     color: COLORS.navy,
//   },

//   /* SUBJECT NAME - CENTERED */

//   academicSubjectCell: {
//     width: "37.5%",
//     justifyContent: "center",
//     alignItems: "center",
//     textAlign: "center",
//     paddingHorizontal: 3,
//     borderRightWidth: 0.6,
//     borderBottomWidth: 0.6,
//     borderColor: COLORS.border,
//     fontSize: 8.5,
//     fontWeight: "bold",
//     color: COLORS.darkGray,
//   },

//   /* PERIOD HEADINGS - NO DARK BACKGROUND */

//   academicScoreHeaderCell: {
//     width: "12.5%",
//     justifyContent: "center",
//     alignItems: "center",
//     textAlign: "center",
//     paddingHorizontal: 1,
//     borderRightWidth: 0.6,
//     borderColor: COLORS.border,
//     fontSize: 6.5,
//     fontWeight: "bold",
//     color: COLORS.navy,
//   },

//   academicScoreHeaderRight: {
//     width: "16.6667%",
//     justifyContent: "center",
//     alignItems: "center",
//     textAlign: "center",
//     paddingHorizontal: 1,
//     borderRightWidth: 0.6,
//     borderColor: COLORS.border,
//     fontSize: 6.5,
//     fontWeight: "bold",
//     color: COLORS.navy,
//   },

//   /* SCORE CELLS */

//   academicScoreCell: {
//     width: "12.5%",
//     justifyContent: "center",
//     alignItems: "center",
//     paddingHorizontal: 1,
//     borderRightWidth: 0.6,
//     borderBottomWidth: 0.6,
//     borderColor: COLORS.border,
//   },

//   academicScoreCellRight: {
//     width: "16.6667%",
//     justifyContent: "center",
//     alignItems: "center",
//     paddingHorizontal: 1,
//     borderRightWidth: 0.6,
//     borderBottomWidth: 0.6,
//     borderColor: COLORS.border,
//   },

//   semesterAverageCell: {
//     backgroundColor: COLORS.lightBlue,
//   },

//   yearlyAverageCell: {
//     backgroundColor: COLORS.lightGold,
//   },

//   /* SCORE CIRCLES */

//   scoreCircle: {
//     width: 22,
//     height: 22,
//     borderRadius: 11,
//     borderWidth: 1.1,
//     justifyContent: "center",
//     alignItems: "center",
//     alignSelf: "center",
//   },

//   scoreCircleText: {
//     fontSize: 8.5,
//     fontWeight: "bold",
//     textAlign: "center",
//   },

//   scoreCircleSmall: {
//     width: 20,
//     height: 20,
//     borderRadius: 10,
//     borderWidth: 1,
//     justifyContent: "center",
//     alignItems: "center",
//     alignSelf: "center",
//   },

//   scoreCircleSmallText: {
//     fontSize: 7.5,
//     fontWeight: "bold",
//     textAlign: "center",
//   },

//   /* PERIOD AVERAGES */

//   periodAverageRow: {
//     flexDirection: "row",
//     alignItems: "center",
//     backgroundColor: COLORS.lightGray,
//     minHeight: 24,
//   },

//   averageLabel: {
//     fontSize: 5.5,
//     color: COLORS.gray,
//     textAlign: "center",
//     marginBottom: 1,
//   },

//   averageText: {
//     fontSize: 7,
//     fontWeight: "bold",
//     textAlign: "center",
//   },

//   /* PERFORMANCE SUMMARY */

//   performanceSummary: {
//     flexDirection: "row",
//     justifyContent: "space-between",
//     alignItems: "stretch",
//     width: "78%",
//     alignSelf: "center",
//     marginTop: 5,
//     marginBottom: 1,
//   },

//   summaryCard: {
//     flex: 1,
//     marginHorizontal: 4,
//     backgroundColor: COLORS.lightBlue,
//     borderWidth: 1,
//     borderColor: COLORS.blueBorder,
//     borderRadius: 4,
//     paddingVertical: 4,
//     alignItems: "center",
//     justifyContent: "center",
//   },

//   summaryCardLast: {
//     flex: 1,
//     marginHorizontal: 4,
//     backgroundColor: COLORS.lightGold,
//     borderWidth: 1,
//     borderColor: COLORS.gold,
//     borderRadius: 4,
//     paddingVertical: 4,
//     alignItems: "center",
//     justifyContent: "center",
//   },

//   summaryLabel: {
//     fontSize: 6,
//     fontWeight: "bold",
//     color: COLORS.gray,
//     marginBottom: 2,
//   },

//   summaryValue: {
//     fontSize: 14,
//     fontWeight: "bold",
//   },

//   /* PROMOTION POLICY */

//   promotionBox: {
//     backgroundColor: COLORS.lightGold,
//     borderLeftWidth: 3,
//     borderLeftColor: COLORS.gold,
//     borderRadius: 3,
//     paddingVertical: 4,
//     paddingHorizontal: 8,
//     marginTop: 4,
//   },

//   promotionTitle: {
//     fontSize: 6.5,
//     fontWeight: "bold",
//     color: COLORS.navy,
//     marginBottom: 2,
//   },

//   promotionText: {
//     fontSize: 6.2,
//     color: COLORS.darkGray,
//     lineHeight: 1.2,
//   },

//   /* REMARK */

//   remarkBox: {
//     flexDirection: "row",
//     alignItems: "center",
//     backgroundColor: COLORS.navy,
//     paddingVertical: 5,
//     paddingHorizontal: 8,
//     marginTop: 4,
//     borderRadius: 3,
//   },

//   remarkLabel: {
//     fontSize: 7,
//     fontWeight: "bold",
//     color: COLORS.gold,
//     marginRight: 5,
//   },

//   remarkText: {
//     flex: 1,
//     fontSize: 7,
//     color: COLORS.white,
//   },

//   /* GRADE LEGEND */

//   gradeLegend: {
//     backgroundColor: COLORS.paleBlue,
//     borderWidth: 1,
//     borderColor: COLORS.blueBorder,
//     paddingVertical: 4,
//     paddingHorizontal: 6,
//     marginTop: 4,
//     textAlign: "center",
//     fontSize: 6,
//     color: COLORS.darkGray,
//     borderRadius: 3,
//   },

//   /* CONDUCT AND RANK */

//   conductRankBar: {
//     flexDirection: "row",
//     justifyContent: "space-between",
//     alignItems: "center",
//     marginTop: 3,
//     paddingVertical: 2,
//   },

//   conductText: {
//     fontSize: 7,
//     fontWeight: "bold",
//     color: COLORS.darkGray,
//   },

//   rankBadge: {
//     backgroundColor: COLORS.navy,
//     borderRadius: 3,
//     paddingHorizontal: 8,
//     paddingVertical: 3,
//   },

//   rankText: {
//     color: COLORS.white,
//     fontSize: 7,
//     fontWeight: "bold",
//   },

//   /* SIGNATURES */

//   signatureSection: {
//     flexDirection: "row",
//     justifyContent: "space-between",
//     backgroundColor: COLORS.paleBlue,
//     borderWidth: 1,
//     borderColor: COLORS.blueBorder,
//     borderRadius: 4,
//     paddingVertical: 4,
//     paddingHorizontal: 35,
//     marginTop: 3,
//   },

//   signatureItem: {
//     width: "35%",
//     alignItems: "center",
//   },

//   signatureLabel: {
//     fontSize: 6,
//     fontWeight: "bold",
//     color: COLORS.gray,
//     marginBottom: 8,
//   },

//   signatureLine: {
//     width: "100%",
//     borderTopWidth: 1,
//     borderTopColor: COLORS.navy,
//     marginBottom: 2,
//   },

//   signatureDate: {
//     fontSize: 5.5,
//     color: COLORS.gray,
//   },

//   /* ==========================================================================
//      BACK PAGE STYLES
//   ========================================================================== */

//   backPageHeader: {
//     flexDirection: "row",
//     alignItems: "center",
//     justifyContent: "space-between",
//     backgroundColor: COLORS.paleBlue,
//     borderWidth: 1,
//     borderColor: COLORS.blueBorder,
//     borderRadius: 7,
//     padding: 9,
//     marginBottom: 9,
//   },

//   backPageHeaderLeft: {
//     flexDirection: "row",
//     alignItems: "center",
//     flex: 1,
//   },

//   backPageLogo: {
//     width: 52,
//     height: 47,
//     objectFit: "contain",
//     marginRight: 10,
//   },

//   backPageTitle: {
//     fontSize: 14,
//     fontWeight: "bold",
//     color: COLORS.navy,
//   },

//   backPageSubtitle: {
//     fontSize: 7.5,
//     color: COLORS.gray,
//     marginTop: 3,
//   },

//   backPageTag: {
//     backgroundColor: COLORS.navy,
//     color: COLORS.white,
//     fontSize: 8,
//     fontWeight: "bold",
//     paddingHorizontal: 11,
//     paddingVertical: 6,
//     borderRadius: 4,
//   },

//   backColumns: {
//     flexDirection: "row",
//     width: "100%",
//     alignItems: "flex-start",
//   },

//   backColumnLeft: {
//     width: "49%",
//     marginRight: "2%",
//   },

//   backColumnRight: {
//     width: "49%",
//   },

//   card: {
//     backgroundColor: COLORS.white,
//     borderWidth: 1,
//     borderColor: COLORS.border,
//     borderRadius: 6,
//     padding: 9,
//     marginBottom: 8,
//   },

//   cardHeader: {
//     flexDirection: "row",
//     alignItems: "center",
//     backgroundColor: COLORS.paleBlue,
//     borderBottomWidth: 1,
//     borderBottomColor: COLORS.blueBorder,
//     marginHorizontal: -9,
//     marginTop: -9,
//     marginBottom: 8,
//     paddingHorizontal: 9,
//     paddingVertical: 6,
//   },

//   cardHeaderAccent: {
//     width: 4,
//     height: 14,
//     backgroundColor: COLORS.gold,
//     marginRight: 7,
//     borderRadius: 2,
//   },

//   cardTitle: {
//     fontSize: 8.5,
//     fontWeight: "bold",
//     color: COLORS.navy,
//   },

//   /* SCHOOL PROFILE */

//   schoolProfileContent: {
//     alignItems: "center",
//     justifyContent: "center",
//   },

//   schoolProfileLogo: {
//     width: 54,
//     height: 54,
//     objectFit: "contain",
//     marginBottom: 5,
//   },

//   schoolProfileName: {
//     fontSize: 10,
//     fontWeight: "bold",
//     color: COLORS.navy,
//     textAlign: "center",
//     marginBottom: 4,
//   },

//   schoolProfileLocation: {
//     fontSize: 7.5,
//     color: COLORS.gray,
//     textAlign: "center",
//     lineHeight: 1.35,
//   },

//   /* STUDENT PROFILE */

//   studentProfileRow: {
//     flexDirection: "row",
//     alignItems: "center",
//   },

//   avatar: {
//     width: 65,
//     height: 65,
//     borderRadius: 5,
//     objectFit: "cover",
//     marginRight: 11,
//     borderWidth: 2,
//     borderColor: COLORS.gold,
//   },

//   studentProfileDetails: {
//     flex: 1,
//   },

//   profileStudentName: {
//     fontSize: 11,
//     fontWeight: "bold",
//     color: COLORS.navy,
//     marginBottom: 5,
//   },

//   profileDetailRow: {
//     flexDirection: "row",
//     marginBottom: 4,
//   },

//   profileDetailLabel: {
//     width: 75,
//     fontSize: 7.5,
//     color: COLORS.gray,
//   },

//   profileDetailValue: {
//     flex: 1,
//     fontSize: 8,
//     fontWeight: "bold",
//     color: COLORS.darkGray,
//   },

//   /* PERFORMANCE CIRCLES */

//   averageSummaryRow: {
//     flexDirection: "row",
//     justifyContent: "space-around",
//     alignItems: "center",
//     paddingVertical: 3,
//   },

//   averageSummaryItem: {
//     width: 90,
//     alignItems: "center",
//   },

//   averageSummaryCircle: {
//     width: 62,
//     height: 62,
//     borderRadius: 31,
//     justifyContent: "center",
//     alignItems: "center",
//     borderWidth: 2,
//     borderColor: COLORS.blueText,
//     backgroundColor: COLORS.lightBlue,
//   },

//   averageSummaryCircleOrange: {
//     width: 62,
//     height: 62,
//     borderRadius: 31,
//     justifyContent: "center",
//     alignItems: "center",
//     borderWidth: 2,
//     borderColor: COLORS.gold,
//     backgroundColor: COLORS.lightGold,
//   },

//   averageSummaryCircleGreen: {
//     width: 62,
//     height: 62,
//     borderRadius: 31,
//     justifyContent: "center",
//     alignItems: "center",
//     borderWidth: 2,
//     borderColor: COLORS.green,
//     backgroundColor: COLORS.lightGreen,
//   },

//   averageSummaryLabel: {
//     fontSize: 7,
//     fontWeight: "bold",
//     textAlign: "center",
//     color: COLORS.gray,
//     marginTop: 5,
//   },

//   averageSummaryValue: {
//     fontSize: 11,
//     fontWeight: "bold",
//   },

//   /* PROMOTION OPTIONS */

//   promotionOption: {
//     flexDirection: "row",
//     alignItems: "center",
//     paddingVertical: 5,
//     borderBottomWidth: 0.5,
//     borderBottomColor: COLORS.border,
//   },

//   promotionLetter: {
//     width: 19,
//     height: 19,
//     borderRadius: 9.5,
//     backgroundColor: COLORS.lightGold,
//     borderWidth: 1,
//     borderColor: COLORS.gold,
//     justifyContent: "center",
//     alignItems: "center",
//     marginRight: 8,
//   },

//   promotionLetterText: {
//     fontSize: 8,
//     fontWeight: "bold",
//     color: COLORS.navy,
//   },

//   promotionOptionText: {
//     flex: 1,
//     fontSize: 8,
//     color: COLORS.darkGray,
//   },

//   promotionLine: {
//     width: 110,
//     borderBottomWidth: 0.8,
//     borderBottomColor: COLORS.navy,
//     marginLeft: 5,
//   },

//   /* BACK PAGE PERIOD AVERAGES */

//   backPeriodAverageRow: {
//     flexDirection: "row",
//     justifyContent: "space-between",
//     alignItems: "center",
//     paddingVertical: 4,
//     borderBottomWidth: 0.5,
//     borderBottomColor: COLORS.border,
//   },

//   periodAverageName: {
//     fontSize: 8,
//     color: COLORS.darkGray,
//   },

//   periodAverageValue: {
//     fontSize: 8.5,
//     fontWeight: "bold",
//   },

//   /* INFORMATION GRID */

//   infoGrid: {
//     flexDirection: "row",
//     flexWrap: "wrap",
//   },

//   infoGridItem: {
//     width: "50%",
//     paddingVertical: 4,
//   },

//   infoGridLabel: {
//     fontSize: 7,
//     color: COLORS.gray,
//     marginBottom: 2,
//   },

//   infoGridValue: {
//     fontSize: 8.5,
//     fontWeight: "bold",
//     color: COLORS.darkGray,
//   },

//   /* PARENT SIGNATURE */

//   parentSignatureCard: {
//     backgroundColor: COLORS.lightGold,
//     borderWidth: 1,
//     borderColor: COLORS.gold,
//     borderRadius: 6,
//     padding: 10,
//     alignItems: "center",
//   },

//   parentSignatureTitle: {
//     fontSize: 8.5,
//     fontWeight: "bold",
//     color: COLORS.navy,
//     marginBottom: 17,
//   },

//   parentSignatureLine: {
//     width: "75%",
//     borderTopWidth: 1,
//     borderTopColor: COLORS.navy,
//     marginBottom: 4,
//   },

//   parentSignatureCaption: {
//     fontSize: 7,
//     color: COLORS.gray,
//   },

//   /* BACK FOOTER */

//   backFooter: {
//     flexDirection: "row",
//     justifyContent: "space-between",
//     alignItems: "center",
//     backgroundColor: COLORS.navy,
//     paddingVertical: 7,
//     paddingHorizontal: 10,
//     borderRadius: 4,
//     marginTop: 2,
//   },

//   backFooterText: {
//     color: COLORS.white,
//     fontSize: 6.5,
//     flex: 1,
//     marginRight: 8,
//   },

//   backFooterHighlight: {
//     color: COLORS.gold,
//     fontSize: 7,
//     fontWeight: "bold",
//   },
// });

// /* ============================================================================
//    DYNAMIC SCORE CIRCLE
// ============================================================================ */

// const ScoreCircle = ({
//   value,
//   size = 22,
//   fontSize = 8.5,
// }) => {
//   const formatted = formatValue(value);

//   return (
//     <View
//       style={{
//         width: size,
//         height: size,
//         borderRadius: size / 2,
//         borderWidth: 1.1,
//         borderColor: getScoreBorder(formatted),
//         backgroundColor: getScoreBackground(formatted),
//         justifyContent: "center",
//         alignItems: "center",
//         alignSelf: "center",
//       }}
//     >
//       <Text
//         style={{
//           fontSize,
//           fontWeight: "bold",
//           textAlign: "center",
//           color: getColor(formatted),
//         }}
//       >
//         {formatted}
//       </Text>
//     </View>
//   );
// };

// /* ============================================================================
//    REPORT CARD COMPONENT
// ============================================================================ */

// const ReportCardPDF = ({
//   responses = [],
//   selectedGradeClass,
//   selectedYear,
//   showRemarksInPDF,
// }) => {
//   return (
//     <Document>

//       {responses.map((res, idx) => {
//         if (!res?.data?.period_grades) {
//           return null;
//         }

//         const data = res.data;

//         /* ================================================================
//            DIVISION DETECTION
//         ================================================================ */

//         const gradeClassName = data.grade_class_name || "";
//         const normalizedClass = gradeClassName.toLowerCase();

//         const division =
//           normalizedClass.includes("nursery") ||
//           normalizedClass.includes("kindergarten") ||
//           normalizedClass.includes("ece")
//             ? "ECE"
//             : "Elementary";

//         /* ================================================================
//            SUBJECT ORDER
//         ================================================================ */

//         const order = SUBJECT_ORDER[division] || [];

//         const dataSubjects = [
//           ...new Set(
//             data.period_grades
//               .map((g) => g.subject_name?.trim())
//               .filter(Boolean)
//           ),
//         ];

//         const extraSubjects = dataSubjects.filter(
//           (subject) =>
//             !order.some(
//               (orderedSubject) =>
//                 orderedSubject.toLowerCase() === subject.toLowerCase()
//             )
//         );

//         const subjects = [
//           ...order.filter((subject) =>
//             dataSubjects.some(
//               (dataSubject) =>
//                 dataSubject.toLowerCase() === subject.toLowerCase()
//             )
//           ),
//           ...extraSubjects,
//         ];

//         /* ================================================================
//            DYNAMIC FRONT-PAGE TABLE SIZING

//            More subjects = shorter rows and smaller circles.
//            This keeps all subjects on the front page.
//         ================================================================ */

//         const subjectCount = subjects.length;

//         let rowHeight = 24;
//         let circleSize = 21;
//         let circleFontSize = 8.5;
//         let subjectFontSize = 8.5;
//         let headerHeight = 23;

//         if (subjectCount >= 13) {
//           rowHeight = 21;
//           circleSize = 18;
//           circleFontSize = 7.5;
//           subjectFontSize = 7.8;
//           headerHeight = 21;
//         }

//         if (subjectCount >= 14) {
//           rowHeight = 19;
//           circleSize = 16;
//           circleFontSize = 6.8;
//           subjectFontSize = 7.2;
//           headerHeight = 20;
//         }

//         if (subjectCount >= 16) {
//           rowHeight = 17;
//           circleSize = 14;
//           circleFontSize = 6;
//           subjectFontSize = 6.5;
//           headerHeight = 18;
//         }

//         /* ================================================================
//            SUBJECT AVERAGES
//         ================================================================ */

//         const getSubjectAverage = (subject, field) => {
//           return (
//             data.subjects_semester_averages?.find(
//               (item) =>
//                 item.subject_name?.toLowerCase() === subject.toLowerCase()
//             )?.[field] ?? "N/A"
//           );
//         };

//         /* ================================================================
//            SUBJECT GRADES
//         ================================================================ */

//         const getSubjectGrades = (subject) => {
//           return data.period_grades.filter(
//             (grade) =>
//               grade.subject_name?.toLowerCase() === subject.toLowerCase()
//           );
//         };

//         /* ================================================================
//            PERIOD SCORE
//         ================================================================ */

//         const getPeriodScore = (subject, period) => {
//           const grade = getSubjectGrades(subject).find(
//             (item) => item.period_name === period
//           );

//           return formatValue(grade?.formatted_final_score);
//         };

//         /* ================================================================
//            STUDENT PHOTO
//         ================================================================ */

//         const studentPhoto =
//           data.student_admission_image || studentAvatar;

//         /* ================================================================
//            DYNAMIC CELL STYLES
//         ================================================================ */

//         const subjectRowStyle = {
//           minHeight: rowHeight,
//           height: rowHeight,
//         };

//         const subjectCellStyle = {
//           minHeight: rowHeight,
//           height: rowHeight,
//           fontSize: subjectFontSize,
//         };

//         const scoreCellStyle = {
//           minHeight: rowHeight,
//           height: rowHeight,
//           paddingVertical: 1,
//         };

//         const headerRowStyle = {
//           minHeight: headerHeight,
//           height: headerHeight,
//         };

//         return (
//           <React.Fragment key={idx}>

//             {/* ============================================================
//                 FRONT PAGE
//             ============================================================ */}

//             <Page
//               size="A4"
//               orientation="landscape"
//               style={styles.page}
//               wrap={false}
//             >

//               <View style={styles.topAccentBar} />

//               {/* SCHOOL HEADER */}

//               <View style={styles.header}>

//                 <View style={styles.logoSection}>
//                   <Image src={logo} style={styles.logo} />

//                   <View style={styles.schoolIdentity}>
//                     <Text style={styles.schoolName}>
//                       Zion Praise Academy
//                     </Text>

//                     <Text style={styles.schoolSubtitle}>
//                       CRM SCHOOL SYSTEM • ACADEMIC EXCELLENCE • CHARACTER • FAITH
//                     </Text>

//                     <Text style={styles.schoolLocation}>
//                       1st Cowfield Junction, Duport Road, Paynesville, Monrovia, Liberia
//                     </Text>
//                   </View>
//                 </View>

//                 <View style={styles.reportTitleSection}>
//                   <Text style={styles.reportCardTitle}>
//                     STUDENT REPORT CARD
//                   </Text>

//                   <Text style={styles.reportCardSubtitle}>
//                     Official Academic Performance Record
//                   </Text>
//                 </View>

//                 <Image
//                   src={studentPhoto}
//                   style={styles.frontAvatar}
//                 />

//               </View>

//               {/* STUDENT INFORMATION */}

//               <View style={styles.studentInfoBar}>

//                 <View style={styles.studentInfoItem}>
//                   <Text style={styles.studentInfoLabel}>
//                     STUDENT NAME
//                   </Text>

//                   <Text style={styles.studentInfoValue}>
//                     {data.student_name || "N/A"}
//                   </Text>
//                 </View>

//                 <View style={styles.studentInfoItem}>
//                   <Text style={styles.studentInfoLabel}>
//                     GRADE CLASS
//                   </Text>

//                   <Text style={styles.studentInfoValue}>
//                     {data.grade_class_name || "N/A"}
//                   </Text>
//                 </View>

//                 <View style={styles.studentInfoItem}>
//                   <Text style={styles.studentInfoLabel}>
//                     ACADEMIC YEAR
//                   </Text>

//                   <Text style={styles.studentInfoValue}>
//                     {selectedYear?.name || "N/A"}
//                   </Text>
//                 </View>

//                 <View style={styles.studentInfoItem}>
//                   <Text style={styles.studentInfoLabel}>
//                     DIVISION
//                   </Text>

//                   <Text style={styles.studentInfoValue}>
//                     {division}
//                   </Text>
//                 </View>

//               </View>

//               {/* ACADEMIC SECTION */}

//               <View style={styles.sectionHeader}>
//                 <Text style={styles.sectionTitle}>
//                   ACADEMIC PERFORMANCE SUMMARY
//                 </Text>

//                 <Text style={styles.sectionTag}>
//                   ANNUAL ASSESSMENT RECORD
//                 </Text>
//               </View>

//               {/* ==========================================================
//                   ACADEMIC TABLE
//               ========================================================== */}

//               <View style={styles.academicTableWrapper}>

//                 {/* SEMESTER ONE */}

//                 <View style={styles.academicHalfTable}>

//                   <View
//                     style={[
//                       styles.academicHeaderRow,
//                       headerRowStyle,
//                     ]}
//                   >

//                     <Text
//                       style={[
//                         styles.academicSubjectHeaderCell,
//                         { height: headerHeight },
//                       ]}
//                     >
//                       SUBJECTS
//                     </Text>

//                     {sem1Periods.map((period) => (
//                       <Text
//                         key={period}
//                         style={[
//                           styles.academicScoreHeaderCell,
//                           { height: headerHeight },
//                         ]}
//                       >
//                         {period}
//                       </Text>
//                     ))}

//                     <Text
//                       style={[
//                         styles.academicScoreHeaderCell,
//                         {
//                           height: headerHeight,
//                           backgroundColor: COLORS.white,
//                           color: COLORS.blueText,
//                         },
//                       ]}
//                     >
//                       SA1
//                     </Text>

//                   </View>

//                   {subjects.map((subject) => {
//                     const sem1Avg = getSubjectAverage(
//                       subject,
//                       "semester_1_avg"
//                     );

//                     return (
//                       <View
//                         key={`sem1-${subject}`}
//                         style={[
//                           styles.academicBodyRow,
//                           subjectRowStyle,
//                         ]}
//                       >

//                         <View
//                           style={[
//                             styles.academicSubjectCell,
//                             subjectCellStyle,
//                           ]}
//                         >
//                           <Text
//                             style={{
//                               fontSize: subjectFontSize,
//                               fontWeight: "bold",
//                               color: COLORS.darkGray,
//                               textAlign: "center",
//                             }}
//                           >
//                             {subject}
//                           </Text>
//                         </View>

//                         {sem1Periods.map((period) => (
//                           <View
//                             key={`${subject}-${period}`}
//                             style={[
//                               styles.academicScoreCell,
//                               scoreCellStyle,
//                             ]}
//                           >
//                             <ScoreCircle
//                               value={getPeriodScore(subject, period)}
//                               size={circleSize}
//                               fontSize={circleFontSize}
//                             />
//                           </View>
//                         ))}

//                         <View
//                           style={[
//                             styles.academicScoreCell,
//                             styles.semesterAverageCell,
//                             scoreCellStyle,
//                           ]}
//                         >
//                           <ScoreCircle
//                             value={sem1Avg}
//                             size={circleSize}
//                             fontSize={circleFontSize}
//                           />
//                         </View>

//                       </View>
//                     );
//                   })}

//                 </View>

//                 {/* SEMESTER TWO */}

//                 <View
//                   style={[
//                     styles.academicHalfTable,
//                     styles.academicHalfTableRight,
//                   ]}
//                 >

//                   <View
//                     style={[
//                       styles.academicHeaderRow,
//                       headerRowStyle,
//                     ]}
//                   >

//                     {sem2Periods.map((period) => (
//                       <Text
//                         key={period}
//                         style={[
//                           styles.academicScoreHeaderRight,
//                           { height: headerHeight },
//                         ]}
//                       >
//                         {period}
//                       </Text>
//                     ))}

//                     <Text
//                       style={[
//                         styles.academicScoreHeaderRight,
//                         {
//                           height: headerHeight,
//                           color: COLORS.blueText,
//                         },
//                       ]}
//                     >
//                       SA2
//                     </Text>

//                     <Text
//                       style={[
//                         styles.academicScoreHeaderRight,
//                         {
//                           height: headerHeight,
//                           color: "#92722A",
//                         },
//                       ]}
//                     >
//                       YEARLY
//                     </Text>

//                   </View>

//                   {subjects.map((subject) => {
//                     const sem2Avg = getSubjectAverage(
//                       subject,
//                       "semester_2_avg"
//                     );

//                     const yearlyAvg = getSubjectAverage(
//                       subject,
//                       "yearly_avg"
//                     );

//                     return (
//                       <View
//                         key={`sem2-${subject}`}
//                         style={[
//                           styles.academicBodyRow,
//                           subjectRowStyle,
//                         ]}
//                       >

//                         {sem2Periods.map((period) => (
//                           <View
//                             key={`${subject}-${period}`}
//                             style={[
//                               styles.academicScoreCellRight,
//                               scoreCellStyle,
//                             ]}
//                           >
//                             <ScoreCircle
//                               value={getPeriodScore(subject, period)}
//                               size={circleSize}
//                               fontSize={circleFontSize}
//                             />
//                           </View>
//                         ))}

//                         <View
//                           style={[
//                             styles.academicScoreCellRight,
//                             styles.semesterAverageCell,
//                             scoreCellStyle,
//                           ]}
//                         >
//                           <ScoreCircle
//                             value={sem2Avg}
//                             size={circleSize}
//                             fontSize={circleFontSize}
//                           />
//                         </View>

//                         <View
//                           style={[
//                             styles.academicScoreCellRight,
//                             styles.yearlyAverageCell,
//                             scoreCellStyle,
//                           ]}
//                         >
//                           <ScoreCircle
//                             value={yearlyAvg}
//                             size={circleSize}
//                             fontSize={circleFontSize}
//                           />
//                         </View>

//                       </View>
//                     );
//                   })}

//                 </View>

//               </View>

//               {/* ==========================================================
//                   PERIOD AVERAGES
//               ========================================================== */}

//               <View style={styles.academicTableWrapper}>

//                 {/* SEMESTER ONE AVERAGES */}

//                 <View style={styles.academicHalfTable}>

//                   <View
//                     style={[
//                       styles.periodAverageRow,
//                       { minHeight: 24 },
//                     ]}
//                   >

//                     <View
//                       style={[
//                         styles.academicSubjectCell,
//                         {
//                           minHeight: 24,
//                           backgroundColor: COLORS.lightGray,
//                         },
//                       ]}
//                     >
//                       <Text
//                         style={{
//                           fontSize: 6.5,
//                           fontWeight: "bold",
//                           color: COLORS.navy,
//                           textAlign: "center",
//                         }}
//                       >
//                         PERIOD AVERAGES
//                       </Text>
//                     </View>

//                     {sem1Periods.map((period) => {
//                       const avg =
//                         data.period_average?.[period] ?? "N/A";

//                       return (
//                         <View
//                           key={period}
//                           style={[
//                             styles.academicScoreCell,
//                             {
//                               minHeight: 24,
//                               backgroundColor: COLORS.lightGray,
//                             },
//                           ]}
//                         >
//                           <ScoreCircle
//                             value={avg}
//                             size={17}
//                             fontSize={6.5}
//                           />
//                         </View>
//                       );
//                     })}

//                     <View
//                       style={[
//                         styles.academicScoreCell,
//                         {
//                           minHeight: 24,
//                           backgroundColor: COLORS.lightBlue,
//                         },
//                       ]}
//                     >
//                       <Text style={styles.averageLabel}>SA1</Text>

//                       <Text
//                         style={[
//                           styles.averageText,
//                           { color: getColor(data.semester_1_avg) },
//                         ]}
//                       >
//                         {formatValue(data.semester_1_avg)}
//                       </Text>
//                     </View>

//                   </View>

//                 </View>

//                 {/* SEMESTER TWO AVERAGES */}

//                 <View
//                   style={[
//                     styles.academicHalfTable,
//                     styles.academicHalfTableRight,
//                   ]}
//                 >

//                   <View
//                     style={[
//                       styles.periodAverageRow,
//                       { minHeight: 24 },
//                     ]}
//                   >

//                     {sem2Periods.map((period) => {
//                       const avg =
//                         data.period_average?.[period] ?? "N/A";

//                       return (
//                         <View
//                           key={period}
//                           style={[
//                             styles.academicScoreCellRight,
//                             {
//                               minHeight: 24,
//                               backgroundColor: COLORS.lightGray,
//                             },
//                           ]}
//                         >
//                           <ScoreCircle
//                             value={avg}
//                             size={17}
//                             fontSize={6.5}
//                           />
//                         </View>
//                       );
//                     })}

//                     <View
//                       style={[
//                         styles.academicScoreCellRight,
//                         {
//                           minHeight: 24,
//                           backgroundColor: COLORS.lightBlue,
//                         },
//                       ]}
//                     >
//                       <Text style={styles.averageLabel}>SA2</Text>

//                       <Text
//                         style={[
//                           styles.averageText,
//                           { color: getColor(data.semester_2_avg) },
//                         ]}
//                       >
//                         {formatValue(data.semester_2_avg)}
//                       </Text>
//                     </View>

//                     <View
//                       style={[
//                         styles.academicScoreCellRight,
//                         {
//                           minHeight: 24,
//                           backgroundColor: COLORS.lightGold,
//                         },
//                       ]}
//                     >
//                       <Text style={styles.averageLabel}>YEARLY</Text>

//                       <Text
//                         style={[
//                           styles.averageText,
//                           { color: getColor(data.yearly_average) },
//                         ]}
//                       >
//                         {formatValue(data.yearly_average)}
//                       </Text>
//                     </View>

//                   </View>

//                 </View>

//               </View>

//               {/* PERFORMANCE SUMMARY */}

//               <View style={styles.performanceSummary}>

//                 <View style={styles.summaryCard}>
//                   <Text style={styles.summaryLabel}>
//                     SEMESTER 1 AVERAGE
//                   </Text>

//                   <Text
//                     style={[
//                       styles.summaryValue,
//                       { color: getColor(data.semester_1_avg) },
//                     ]}
//                   >
//                     {formatValue(data.semester_1_avg)}
//                   </Text>
//                 </View>

//                 <View style={styles.summaryCard}>
//                   <Text style={styles.summaryLabel}>
//                     SEMESTER 2 AVERAGE
//                   </Text>

//                   <Text
//                     style={[
//                       styles.summaryValue,
//                       { color: getColor(data.semester_2_avg) },
//                     ]}
//                   >
//                     {formatValue(data.semester_2_avg)}
//                   </Text>
//                 </View>

//                 <View style={styles.summaryCardLast}>
//                   <Text style={styles.summaryLabel}>
//                     FINAL YEARLY AVERAGE
//                   </Text>

//                   <Text
//                     style={[
//                       styles.summaryValue,
//                       { color: getColor(data.yearly_average) },
//                     ]}
//                   >
//                     {formatValue(data.yearly_average)}
//                   </Text>
//                 </View>

//               </View>

//               {/* PROMOTION POLICY */}

//               <View style={styles.promotionBox}>
//                 <Text style={styles.promotionTitle}>
//                   PROMOTION POLICY
//                 </Text>

//                 <Text style={styles.promotionText}>
//                   A student who fails two major subjects may not be eligible
//                   for promotion. A student who fails one major subject may
//                   be required to attend vacation school or complete an
//                   approved academic intervention program.
//                 </Text>
//               </View>

//               {/* FINAL REMARK */}

//               <View style={styles.remarkBox}>
//                 <Text style={styles.remarkLabel}>
//                   FINAL REMARK:
//                 </Text>

//                 <Text style={styles.remarkText}>
//                   {showRemarksInPDF
//                     ? data.remark || "No remark provided."
//                     : "Year-End Remark will appear here after review."}
//                 </Text>
//               </View>

//               {/* GRADING SCALE */}

//               <Text style={styles.gradeLegend}>
//                 GRADING SCALE: 60–69 FAIL | 70–79 FAIR | 80–89 GOOD |
//                 90–95 VERY GOOD | 96–100 EXCELLENT
//               </Text>

//               {/* CONDUCT AND RANK */}

//               <View style={styles.conductRankBar}>
//                 <Text style={styles.conductText}>
//                   YEARLY CONDUCT: {data.yearly_conduct || "N/A"}
//                 </Text>

//                 <View style={styles.rankBadge}>
//                   <Text style={styles.rankText}>
//                     CLASS RANK: {data.rank || "N/A"}
//                   </Text>
//                 </View>
//               </View>

//               {/* SIGNATURES */}

//               <View style={styles.signatureSection}>

//                 <View style={styles.signatureItem}>
//                   <Text style={styles.signatureLabel}>
//                     PRINCIPAL'S SIGNATURE
//                   </Text>

//                   <View style={styles.signatureLine} />

//                   <Text style={styles.signatureDate}>
//                     Date: ____________________
//                   </Text>
//                 </View>

//                 <View style={styles.signatureItem}>
//                   <Text style={styles.signatureLabel}>
//                     CLASS SPONSOR'S SIGNATURE
//                   </Text>

//                   <View style={styles.signatureLine} />

//                   <Text style={styles.signatureDate}>
//                     Date: ____________________
//                   </Text>
//                 </View>

//               </View>

//             </Page>

//             {/* ============================================================
//                 BACK PAGE
//                 Exactly one back page per student.
//             ============================================================ */}

//             <Page
//               size="A4"
//               orientation="landscape"
//               style={styles.backPage}
//               wrap={false}
//             >

//               {/* BACK HEADER */}

//               <View style={styles.backPageHeader}>

//                 <View style={styles.backPageHeaderLeft}>
//                   <Image src={logo} style={styles.backPageLogo} />

//                   <View>
//                     <Text style={styles.backPageTitle}>
//                       STUDENT ACADEMIC PROFILE
//                     </Text>

//                     <Text style={styles.backPageSubtitle}>
//                       Official School Record • Student Development • Promotion
//                     </Text>
//                   </View>
//                 </View>

//                 <Text style={styles.backPageTag}>
//                   BACK PAGE
//                 </Text>

//               </View>

//               {/* TWO COLUMNS */}

//               <View style={styles.backColumns}>

//                 {/* LEFT COLUMN */}

//                 <View style={styles.backColumnLeft}>

//                   {/* SCHOOL INFORMATION */}

//                   <View style={styles.card}>

//                     <View style={styles.cardHeader}>
//                       <View style={styles.cardHeaderAccent} />

//                       <Text style={styles.cardTitle}>
//                         SCHOOL INFORMATION
//                       </Text>
//                     </View>

//                     <View style={styles.schoolProfileContent}>

//                       <Image
//                         src={logo}
//                         style={styles.schoolProfileLogo}
//                       />

//                       <Text style={styles.schoolProfileName}>
//                         Zion Praise Academy - CRM School System
//                       </Text>

//                       <Text style={styles.schoolProfileLocation}>
//                         Cowfield 1st Junction, Duport Road,
//                         Paynesville, Monrovia, Liberia
//                       </Text>

//                       <View style={styles.infoGrid}>

//                         <View style={styles.infoGridItem}>
//                           <Text style={styles.infoGridLabel}>
//                             SCHOOL YEAR
//                           </Text>

//                           <Text style={styles.infoGridValue}>
//                             {selectedYear?.name || "N/A"}
//                           </Text>
//                         </View>

//                         <View style={styles.infoGridItem}>
//                           <Text style={styles.infoGridLabel}>
//                             DIVISION
//                           </Text>

//                           <Text style={styles.infoGridValue}>
//                             {division}
//                           </Text>
//                         </View>

//                       </View>

//                     </View>

//                   </View>

//                   {/* STUDENT PROFILE */}

//                   <View style={styles.card}>

//                     <View style={styles.cardHeader}>
//                       <View style={styles.cardHeaderAccent} />

//                       <Text style={styles.cardTitle}>
//                         STUDENT PROFILE
//                       </Text>
//                     </View>

//                     <View style={styles.studentProfileRow}>

//                       <Image
//                         src={studentPhoto}
//                         style={styles.avatar}
//                       />

//                       <View style={styles.studentProfileDetails}>

//                         <Text style={styles.profileStudentName}>
//                           {data.student_name || "N/A"}
//                         </Text>

//                         <View style={styles.profileDetailRow}>
//                           <Text style={styles.profileDetailLabel}>
//                             Class:
//                           </Text>

//                           <Text style={styles.profileDetailValue}>
//                             {data.grade_class_name || "N/A"}
//                           </Text>
//                         </View>

//                         <View style={styles.profileDetailRow}>
//                           <Text style={styles.profileDetailLabel}>
//                             Academic Year:
//                           </Text>

//                           <Text style={styles.profileDetailValue}>
//                             {selectedYear?.name || "N/A"}
//                           </Text>
//                         </View>

//                         <View style={styles.profileDetailRow}>
//                           <Text style={styles.profileDetailLabel}>
//                             Rank:
//                           </Text>

//                           <Text style={styles.profileDetailValue}>
//                             {data.rank || "N/A"}
//                           </Text>
//                         </View>

//                       </View>

//                     </View>

//                   </View>

//                   {/* PERFORMANCE OVERVIEW */}

//                   <View style={styles.card}>

//                     <View style={styles.cardHeader}>
//                       <View style={styles.cardHeaderAccent} />

//                       <Text style={styles.cardTitle}>
//                         PERFORMANCE OVERVIEW
//                       </Text>
//                     </View>

//                     <View style={styles.averageSummaryRow}>

//                       <View style={styles.averageSummaryItem}>
//                         <View style={styles.averageSummaryCircle}>
//                           <Text
//                             style={[
//                               styles.averageSummaryValue,
//                               { color: getColor(data.semester_1_avg) },
//                             ]}
//                           >
//                             {formatValue(data.semester_1_avg)}
//                           </Text>
//                         </View>

//                         <Text style={styles.averageSummaryLabel}>
//                           SEMESTER 1
//                         </Text>
//                       </View>

//                       <View style={styles.averageSummaryItem}>
//                         <View style={styles.averageSummaryCircleOrange}>
//                           <Text
//                             style={[
//                               styles.averageSummaryValue,
//                               { color: getColor(data.semester_2_avg) },
//                             ]}
//                           >
//                             {formatValue(data.semester_2_avg)}
//                           </Text>
//                         </View>

//                         <Text style={styles.averageSummaryLabel}>
//                           SEMESTER 2
//                         </Text>
//                       </View>

//                       <View style={styles.averageSummaryItem}>
//                         <View style={styles.averageSummaryCircleGreen}>
//                           <Text
//                             style={[
//                               styles.averageSummaryValue,
//                               { color: getColor(data.yearly_average) },
//                             ]}
//                           >
//                             {formatValue(data.yearly_average)}
//                           </Text>
//                         </View>

//                         <Text style={styles.averageSummaryLabel}>
//                           FINAL YEARLY
//                         </Text>
//                       </View>

//                     </View>

//                   </View>

//                   {/* STUDENT DEVELOPMENT */}

//                   <View style={styles.card}>

//                     <View style={styles.cardHeader}>
//                       <View style={styles.cardHeaderAccent} />

//                       <Text style={styles.cardTitle}>
//                         STUDENT DEVELOPMENT
//                       </Text>
//                     </View>

//                     <View style={styles.infoGrid}>

//                       <View style={styles.infoGridItem}>
//                         <Text style={styles.infoGridLabel}>
//                           YEARLY CONDUCT
//                         </Text>

//                         <Text style={styles.infoGridValue}>
//                           {data.yearly_conduct || "N/A"}
//                         </Text>
//                       </View>

//                       <View style={styles.infoGridItem}>
//                         <Text style={styles.infoGridLabel}>
//                           CLASS RANK
//                         </Text>

//                         <Text style={styles.infoGridValue}>
//                           {data.rank || "N/A"}
//                         </Text>
//                       </View>

//                     </View>

//                   </View>

//                 </View>

//                 {/* RIGHT COLUMN */}

//                 <View style={styles.backColumnRight}>

//                   {/* PROMOTION DECISION */}

//                   <View style={styles.card}>

//                     <View style={styles.cardHeader}>
//                       <View style={styles.cardHeaderAccent} />

//                       <Text style={styles.cardTitle}>
//                         PROMOTION DECISION
//                       </Text>
//                     </View>

//                     {[
//                       ["A", "Promoted to:"],
//                       ["B", "Condition in:"],
//                       ["C", "Fail / Repeat Grade Class"],
//                       ["D", "Never to Return"],
//                     ].map(([letter, label]) => (
//                       <View
//                         key={letter}
//                         style={styles.promotionOption}
//                       >
//                         <View style={styles.promotionLetter}>
//                           <Text style={styles.promotionLetterText}>
//                             {letter}
//                           </Text>
//                         </View>

//                         <Text style={styles.promotionOptionText}>
//                           {label}
//                         </Text>

//                         {letter !== "C" && letter !== "D" && (
//                           <View style={styles.promotionLine} />
//                         )}
//                       </View>
//                     ))}

//                   </View>

//                   {/* PERIOD AVERAGES */}

//                   <View style={styles.card}>

//                     <View style={styles.cardHeader}>
//                       <View style={styles.cardHeaderAccent} />

//                       <Text style={styles.cardTitle}>
//                         PERIOD AVERAGES
//                       </Text>
//                     </View>

//                     {allPeriods.map((period) => (
//                       <View
//                         key={period}
//                         style={styles.backPeriodAverageRow}
//                       >
//                         <Text style={styles.periodAverageName}>
//                           {period}
//                         </Text>

//                         <Text
//                           style={[
//                             styles.periodAverageValue,
//                             {
//                               color: getColor(
//                                 data.period_average?.[period]
//                               ),
//                             },
//                           ]}
//                         >
//                           {formatValue(
//                             data.period_average?.[period]
//                           )}
//                         </Text>
//                       </View>
//                     ))}

//                   </View>

//                   {/* FINAL ACADEMIC SUMMARY */}

//                   <View style={styles.card}>

//                     <View style={styles.cardHeader}>
//                       <View style={styles.cardHeaderAccent} />

//                       <Text style={styles.cardTitle}>
//                         FINAL ACADEMIC SUMMARY
//                       </Text>
//                     </View>

//                     <View style={styles.infoGrid}>

//                       <View style={styles.infoGridItem}>
//                         <Text style={styles.infoGridLabel}>
//                           SEMESTER 1 AVERAGE
//                         </Text>

//                         <Text
//                           style={[
//                             styles.infoGridValue,
//                             { color: getColor(data.semester_1_avg) },
//                           ]}
//                         >
//                           {formatValue(data.semester_1_avg)}
//                         </Text>
//                       </View>

//                       <View style={styles.infoGridItem}>
//                         <Text style={styles.infoGridLabel}>
//                           SEMESTER 2 AVERAGE
//                         </Text>

//                         <Text
//                           style={[
//                             styles.infoGridValue,
//                             { color: getColor(data.semester_2_avg) },
//                           ]}
//                         >
//                           {formatValue(data.semester_2_avg)}
//                         </Text>
//                       </View>

//                       <View style={styles.infoGridItem}>
//                         <Text style={styles.infoGridLabel}>
//                           YEARLY AVERAGE
//                         </Text>

//                         <Text
//                           style={[
//                             styles.infoGridValue,
//                             { color: getColor(data.yearly_average) },
//                           ]}
//                         >
//                           {formatValue(data.yearly_average)}
//                         </Text>
//                       </View>

//                       <View style={styles.infoGridItem}>
//                         <Text style={styles.infoGridLabel}>
//                           CLASS RANK
//                         </Text>

//                         <Text style={styles.infoGridValue}>
//                           {data.rank || "N/A"}
//                         </Text>
//                       </View>

//                     </View>

//                   </View>

//                   {/* PARENT SIGNATURE */}

//                   <View style={styles.parentSignatureCard}>

//                     <Text style={styles.parentSignatureTitle}>
//                       PARENT / GUARDIAN SIGNATURE
//                     </Text>

//                     <View style={styles.parentSignatureLine} />

//                     <Text style={styles.parentSignatureCaption}>
//                       Signature and Date
//                     </Text>

//                   </View>

//                 </View>

//               </View>

//               {/* BACK FOOTER */}

//               <View style={styles.backFooter}>

//                 <Text style={styles.backFooterText}>
//                   This report card is an official academic record of the school.
//                   Design by: Polu Integrated Technology - School Management System.
//                 </Text>

//                 <Text style={styles.backFooterHighlight}>
//                   EDUCATION • CHARACTER • EXCELLENCE
//                 </Text>

//               </View>

//             </Page>

//           </React.Fragment>
//         );
//       })}

//     </Document>
//   );
// };

// export default ReportCardPDF;